import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    TextField, Button, Grid, Checkbox, FormControlLabel, Typography,
    InputLabel, FormControl, Select, MenuItem, RadioGroup, Radio, FormLabel,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, Paper,
    IconButton, Autocomplete, Modal, Box, CircularProgress, Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Add, Email, Info, PrintOutlined, PrintTwoTone, WhatsApp, Visibility, Remove } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useUser } from '../contexts/UserContext';
import { useSucursal } from '../contexts/SucursalContext';
import useCajaAbiertaValidation from '../hooks/useCajaAbiertaValidation';
import FacturaFormSection from './FacturaFormSection';
import { useNavigate } from 'react-router-dom';
import { useAccesos } from '../contexts/AccesosContext';

function GestionFactura({ datosFactura, onClose }) {
    const toSafeNumber = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const toCents = (value) => Math.round((toSafeNumber(value) + Number.EPSILON) * 100);
    const centsToAmount = (cents) => cents / 100;
    const roundAmount = (value) => centsToAmount(toCents(value));
    const sumAmounts = (values) => centsToAmount(values.reduce((acc, value) => acc + toCents(value), 0));

    // console.log("DATOS FACTURA: ", datosFactura)
    const { tienePermiso } = useAccesos();
    const server = process.env.REACT_APP_API_ROUTE;
    const key = process.env.REACT_APP_API_KEY;
  
    const navigate = useNavigate();

    // Estados para los campos de entrada
    const [ordenTrabajo, setOrdenTrabajo] = useState('');
    const [nombreCliente, setNombreCliente] = useState('');
    const [idCliente, setIdCliente] = useState('');
    const [identidad, setIdentidad] = useState('');
    const [puntosActuales, setPuntosActuales] = useState(0);
    const [lempirasActuales, setLempirasActuales] = useState(0);
    // Estado para recibos asociados a facturas
    const [recibos, setRecibos] = useState([]);
    // Estado para diálogo de nuevo recibo
    const [openNuevoRecibo, setOpenNuevoRecibo] = useState(false);
    const [nuevoReciboData, setNuevoReciboData] = useState({
        idCliente: '',
        valorRecibo: 0,
        saldoAnterior: 0,
        saldoActual: 0,
        concepto: '',
        detalles: [],
        documentosPago: []
    });
    
    // Estados para el modal de detalles de pago
    const [openDetallesPago, setOpenDetallesPago] = useState(false);
    const [detallesPago, setDetallesPago] = useState([]);
    const [facturaDetallesPago, setFacturaDetallesPago] = useState(null);
    const [loadingDetallesPago, setLoadingDetallesPago] = useState(false);
    const [editandoDetallesPago, setEditandoDetallesPago] = useState(false);
    const [detallesPagoEditados, setDetallesPagoEditados] = useState([]);
    
    // Estados para notas de crédito (devoluciones)
    const [notasCredito, setNotasCredito] = useState([]);
    const [openNuevaNotaCredito, setOpenNuevaNotaCredito] = useState(false);
    const [nuevaNotaCreditoData, setNuevaNotaCreditoData] = useState({
        idCliente: '',
        valorNotaCredito: 0,
        motivo: '',
        detalles: [],
        documentosDevolucion: [] // Los métodos de pago se convierten en devoluciones
    });
    
    const [redimirPuntos, setRedimirPuntos] = useState(false);
    const [valorPagar, setValorPagar] = useState(0);
    const [montoConfirmado, setMontoConfirmado] = useState(false);
    const [ordenCompra, setOrdenCompra] = useState('');
    const [ordenCompraExcenta, setOrdenCompraExcenta] = useState('');
    const [constanciaRegistro, setConstanciaRegistro] = useState('');
    const [registroSAG, setRegistroSAG] = useState('');
    const [tipoDocumento, setTipoDocumento] = useState('');
    const [numero, setNumero] = useState('');
    const [valorPagado, setValorPagado] = useState('');
    const [documentos, setDocumentos] = useState([]);
    const [transaccionesCajero, setTransaccionesCajero] = useState([]);
    const [tiposDocumentos, setTiposDocumentos] = useState([]);
    const [cambio, setCambio] = useState(0);
    const [tipoComprobante, setTipoComprobante] = useState('Ticket');
    const [idSucursal, setIdSucursal] = useState('');
    const [idFormaPago, setIdFormaPago] = useState('');
    const { user } = useUser();
    const { cajaAbiertaDatos, verificarAperturaCaja, validarOperacionCritica } = useSucursal();

    const { validarCajaAbierta, cajaAbierta } = useCajaAbiertaValidation();
    // Estado para almacenar las facturas
    const [facturas, setFacturas] = useState([]);
    // Estados para manejo de montos pendientes y totales
    const [montoPendiente, setMontoPendiente] = useState(0);
    const [valorTotalOrden, setValorTotalOrden] = useState(0);
    const [estadoFacturacion, setEstadoFacturacion] = useState('sin-facturar');



    const [dividirFactura, setDividirFactura] = useState(false);
    const [clienteSecundario, setClienteSecundario] = useState(null);


    const [clientes, setClientes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [porcentajeClientePrincipal, setPorcentajeClientePrincipal] = useState(50);
    const [porcentajeClienteSecundario, setPorcentajeClienteSecundario] = useState(50);
    const [montoClientePrincipal, setMontoClientePrincipal] = useState(0);
    const [montoClienteSecundario, setMontoClienteSecundario] = useState(0);
    const [campoEditado, setCampoEditado] = useState(null);
    const [despachando, setDespachando] = useState(false);
    // Estados para vista previa de PDF de factura
    const [openFacturaPreview, setOpenFacturaPreview] = useState(false);
    const [facturaPdfUrl, setFacturaPdfUrl] = useState(null);
    const [facturaPreviewLoading, setFacturaPreviewLoading] = useState(false);
    const [facturaPreviewError, setFacturaPreviewError] = useState(null);
    const [facturaRecienCreada, setFacturaRecienCreada] = useState(null);
    const [facturaPreviewFormato, setFacturaPreviewFormato] = useState('Carta');
    const [originalValorFactura, setOriginalValorFactura] = useState(0);

    const [ordenesSeleccionadas, setOrdenesSeleccionadas] = useState([]);

    const [totalOrdenes, setTotalOrdenes] = useState(0);
    const [pendienteTotal, setPendienteTotal] = useState(0);

    // Estado para la póliza de seguro
    const [poliza, setPoliza] = useState(null);
    // Controlar si hay que mostrar las opciones de seguro
    const [showPolizaOptions, setShowPolizaOptions] = useState(false);
    // Opción seleccionada: 'ambas' | 'aseguradora' | 'asegurado'
    const [polizaOption, setPolizaOption] = useState('ambas');
    const [polizaSplitOption, setPolizaSplitOption] = useState('noElegibleCopago');
    const [clienteOrigenChecked, setClienteOrigenChecked] = useState(false);
    const [origenes, setOrigenes] = useState([]);
    const [origen, setOrigen] = useState('');
    const [tipoFactura, setTipoFactura] = useState('contado'); // 'contado' | 'credito'

    // — PARA CLIENTE PRINCIPAL —
    const [docPrincipal, setDocPrincipal] = useState([]);
    const [tipoDocPrincipal, setTipoDocPrincipal] = useState('');
    const [numPrincipal, setNumPrincipal] = useState('');
    const [valorPagadoPrincipal, setValorPagadoPrincipal] = useState(0);
    const [lempirasPrincipal, setLempirasPrincipal] = useState(lempirasActuales);

    // — PARA CLIENTE SECUNDARIO —
    const [docSecundario, setDocSecundario] = useState([]);
    const [tipoDocSecundario, setTipoDocSecundario] = useState('');
    const [numSecundario, setNumSecundario] = useState('');
    const [valorPagadoSecundario, setValorPagadoSecundario] = useState(0);
    const [lempirasSecundario, setLempirasSecundario] = useState(0);

    // — controles específicos para Cliente Principal —
    const [clienteOrigenCheckedPr, setClienteOrigenCheckedPr] = useState(false);
    const [montoConfirmadoPr, setMontoConfirmadoPr] = useState(false);
    const [tipoFacturaPr, setTipoFacturaPr] = useState('contado');
    const [ordenCompraPr, setOrdenCompraPr] = useState('');
    const [ordenCompraExcentaPr, setOrdenCompraExcentaPr] = useState('');
    const [constanciaRegistroPr, setConstanciaRegistroPr] = useState('');
    const [registroSAGPr, setRegistroSAGPr] = useState('');
    const [tipoComprobantePr, setTipoComprobantePr] = useState('Ticket');

    // — controles específicos para Cliente Secundario —
    const [clienteOrigenCheckedSec, setClienteOrigenCheckedSec] = useState(false);
    const [montoConfirmadoSec, setMontoConfirmadoSec] = useState(false);
    const [tipoFacturaSec, setTipoFacturaSec] = useState('contado');
    const [ordenCompraSec, setOrdenCompraSec] = useState('');
    const [ordenCompraExcentaSec, setOrdenCompraExcentaSec] = useState('');
    const [constanciaRegistroSec, setConstanciaRegistroSec] = useState('');
    const [registroSAGSec, setRegistroSAGSec] = useState('');
    const [tipoComprobanteSec, setTipoComprobanteSec] = useState('Ticket');

    // para Cliente Principal
    const [cambioPrincipal, setCambioPrincipal] = useState(0);
    // para Cliente Secundario
    const [cambioSecundario, setCambioSecundario] = useState(0);

    const [identidadSec, setIdentidadSec] = useState('');
    const [puntosActualesSec, setPuntosActualesSec] = useState(0);
    const [porcentajeCopago, setPorcentajeCopago] = useState(0);
    const [porcentajeAseguradora, setPorcentajeAseguradora] = useState(100);

    // Estados para días de crédito
    const [diasCredito, setDiasCredito] = useState(30);
    const [diasCreditoPr, setDiasCreditoPr] = useState(30);
    const [diasCreditoSec, setDiasCreditoSec] = useState(30);

    // Permiso para modificar libremente los montos en detalles de pago (false = con validación, true = sin validación)
    const PERMITIR_MODIFICACION_LIBRE_MONTOS = false;

    useEffect(() => {
        if (!clienteSecundario) return;
        setIdentidadSec(clienteSecundario.identidad);
        fetchPuntosLempiras(clienteSecundario.idCliente)
            .then(({ saldo, lempiras }) => {
                setPuntosActualesSec(saldo);
                setLempirasSecundario(lempiras);
            })
            .catch(err => console.error(err));
    }, [clienteSecundario]);

    // Dentro de tu componente padre
    const addDocumento = ({
        tipoDocumento,
        valorPagado,
        lempirasActuales,
        setLempirasActuales,
        documentos,
        setDocumentos,
        numero
    }) => {
        if (!tipoDocumento || !valorPagado) {
            alert('Por favor complete el tipo de documento y el valor pagado.');
            return;
        }
        if (valorPagado <= 0) {
            alert('El valor pagado no puede ser menor o igual a 0.');
            return;
        }

        const descripcion = transaccionesCajero.find(d => d.id === tipoDocumento)
            ?.descripcionTipoTransaccion;
        if (
            descripcion?.toLowerCase().includes('puntos') &&
            parseFloat(valorPagado) > parseFloat(lempirasActuales)
        ) {
            alert('El valor pagado no puede ser mayor que el saldo de lempiras actuales.');
            return;
        }

        let nuevoSaldo = lempirasActuales;
        if (descripcion?.toLowerCase().includes('puntos')) {
            nuevoSaldo = (parseFloat(lempirasActuales) - parseFloat(valorPagado)).toFixed(2);
            setLempirasActuales(nuevoSaldo);
        }

        const nuevoDocumento = {
            descripcion,
            tipoDocumento,
            numero,
            valorPagado: parseFloat(valorPagado),
        };
        setDocumentos([...documentos, nuevoDocumento]);
    };

    const removeDocumento = ({
        documentos,
        setDocumentos,
        lempirasActuales,
        setLempirasActuales,
        index
    }) => {
        const doc = documentos[index];
        if (doc.descripcion.toLowerCase().includes('puntos')) {
            setLempirasActuales(prev => (parseFloat(prev) + doc.valorPagado).toFixed(2));
        }
        setDocumentos(documentos.filter((_, i) => i !== index));
    };



    // Clave derivada para detectar cambios aunque el objeto se reuse (evita tener que reabrir otra orden)
    const datosFacturaVersion = useMemo(() => {
        if (!datosFactura) return '';
        const df = datosFactura;
        const detallesLen = Array.isArray(df.detalles?.$values)
            ? df.detalles.$values.length
            : Array.isArray(df.detalles)
                ? df.detalles.length
                : '';
        return [
            df.ordenTrabajo ?? '',
            df.valorVenta ?? '',
            df.valorDescuento ?? '',
            df.valorPagar ?? '',
            df.TotalPagar ?? '',
            detallesLen,
            df.idCliente ?? '',
            df.idSucursal ?? '',
        ].join('|');
    }, [datosFactura]);

    useEffect(() => {
        if (!datosFactura) return;

        // Resetear estado de facturas cuando cambia la orden o cualquiera de los valores relevantes
        facturasCargadas.current = false;
        setFacturas([]);
        setEstadoFacturacion('sin-facturar');
        setPendienteTotal(0);
        setMontoPendiente(0);

        console.log('datosFactura', datosFactura);
        console.log('datosFactura.ordenes', datosFactura.ordenes);

        if (datosFactura.multiple && Array.isArray(datosFactura.ordenes)) {
            setOrdenesSeleccionadas(datosFactura.ordenes);
            const suma = datosFactura.ordenes.reduce((acc, o) => {
                const v = parseFloat(o.valorPagar ?? o.valorVenta ?? o.TotalPagar);
                return acc + (isNaN(v) ? 0 : v);
            }, 0);
            setOriginalValorFactura(suma);
            setValorPagar(suma);
            setIdSucursal(datosFactura.idSucursal || '');
            setIdFormaPago(datosFactura.idFormaPago || '');
            return;
        }

        setOrdenTrabajo(datosFactura.ordenTrabajo || '');
        setNombreCliente(datosFactura.nombreCliente || '');
        setIdentidad(datosFactura.identidad || '');
        setIdCliente(datosFactura.idCliente || '');
        setIdSucursal(datosFactura.idSucursal || '');

        const valor = Number(datosFactura.valorPagar ?? datosFactura.valorVenta ?? datosFactura.TotalPagar ?? 0);
        setOriginalValorFactura(valor);
        setValorPagar(valor);
    }, [datosFacturaVersion]);

useEffect(() => {
    if (!idCliente) return;

    fetchPuntosLempiras(idCliente);
}, [idCliente]);




    const submitHandler = () => {
        if (tipoFactura === 'contado') {
            handleDespachar();
        } else {
            handleFacturaCredito();
        }
    };

    // Tabla resumen de órdenes
    const resumenOrdenes = ordenesSeleccionadas.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 2, p: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
                Órdenes a facturar:
            </Typography>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell># Orden</TableCell>
                        <TableCell>Cliente</TableCell>
                        <TableCell align="right">Valor a Pagar</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {ordenesSeleccionadas.map((o, i) => (
                        <TableRow key={i}>
                            <TableCell>{o.ordenTrabajo}</TableCell>
                            <TableCell>{o.nombreCliente}</TableCell>
                            <TableCell align="right">
                                L. {(parseFloat(o.valorPagar) || 0).toFixed(2)}
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow>
                        <TableCell colSpan={2}><strong>Total</strong></TableCell>
                        <TableCell align="right">
                            <strong>L. {parseFloat(valorPagar).toFixed(2)}</strong>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </Paper>
    );

    useEffect(() => {
        if (dividirFactura && valorPagar && !poliza) {
            const mitad = parseFloat(valorPagar) / 2;
            setMontoClientePrincipal(mitad.toFixed(2));
            setMontoClienteSecundario(mitad.toFixed(2));
            setPorcentajeClientePrincipal(50);
            setPorcentajeClienteSecundario(50);
        } else {
            setMontoClientePrincipal(valorPagar);
            setMontoClienteSecundario(0);
            setPorcentajeClientePrincipal(100);
            setPorcentajeClienteSecundario(0);
        }
    }, [dividirFactura, valorPagar, poliza]);

    // recalcula cambio del Principal
    useEffect(() => {
        const totalDocs = docPrincipal.reduce((sum, d) => sum + d.valorPagado, 0);
        const monto = parseFloat(montoClientePrincipal) || 0;
        // guardamos un número redondeado a dos decimales
        const diff = totalDocs - monto;
        setCambioPrincipal(Math.round(diff * 100) / 100);
    }, [docPrincipal, montoClientePrincipal]);

    // recalcula cambio del Secundario
    useEffect(() => {
        const totalDocs = docSecundario.reduce((sum, d) => sum + d.valorPagado, 0);
        const monto = parseFloat(montoClienteSecundario) || 0;
        const diff = totalDocs - monto;
        setCambioSecundario(Math.round(diff * 100) / 100);
    }, [docSecundario, montoClienteSecundario]);


    useEffect(() => {
        if (!dividirFactura) return;

        if (campoEditado === 'porcentajePrincipal') {
            const nuevoMontoPrincipal = (porcentajeClientePrincipal / 100) * valorPagar;
            const nuevoMontoSecundario = valorPagar - nuevoMontoPrincipal;
            setMontoClientePrincipal(nuevoMontoPrincipal);
            setMontoClienteSecundario(nuevoMontoSecundario);
            setPorcentajeClienteSecundario(100 - porcentajeClientePrincipal);
        } else if (campoEditado === 'porcentajeSecundario') {
            const nuevoMontoSecundario = (porcentajeClienteSecundario / 100) * valorPagar;
            const nuevoMontoPrincipal = valorPagar - nuevoMontoSecundario;
            setMontoClienteSecundario(nuevoMontoSecundario);
            setMontoClientePrincipal(nuevoMontoPrincipal);
            setPorcentajeClientePrincipal(100 - porcentajeClienteSecundario);
        } else if (campoEditado === 'montoPrincipal') {
            const nuevoPorcentajePrincipal = (montoClientePrincipal * 100) / valorPagar;
            const nuevoMontoSecundario = valorPagar - montoClientePrincipal;
            setPorcentajeClientePrincipal(nuevoPorcentajePrincipal);
            setPorcentajeClienteSecundario(100 - nuevoPorcentajePrincipal);
            setMontoClienteSecundario(nuevoMontoSecundario);
        } else if (campoEditado === 'montoSecundario') {
            const nuevoPorcentajeSecundario = (montoClienteSecundario * 100) / valorPagar;
            const nuevoMontoPrincipal = valorPagar - montoClienteSecundario;
            setPorcentajeClienteSecundario(nuevoPorcentajeSecundario);
            setPorcentajeClientePrincipal(100 - nuevoPorcentajeSecundario);
            setMontoClientePrincipal(nuevoMontoPrincipal);
        }

        setCampoEditado(null); // Reset para evitar bucles infinitos
    }, [porcentajeClientePrincipal, porcentajeClienteSecundario, montoClientePrincipal, montoClienteSecundario, dividirFactura]);



    const fetchClientes = async (search = '') => {
        try {
            setIsLoading(true);
            const response = await fetch(`${server}/MgClientes/buscador/${search}`);
            const data = await response.json();
            if (data && data.clientes && Array.isArray(data.clientes.$values)) {
                setClientes(data.clientes.$values);
            }
        } catch (error) {
            console.error('Error al cargar clientes:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchOrigen = async () => {
        try {
            const response = await fetch(`${server}/MgOrigenes`);
            const data = await response.json();
            setOrigenes(data.$values);
        } catch (error) {
            console.error('Error al cargar los orígenes:', error);
        }
    };

    const obtenerNumeroFactura = async () => {
        const response = await fetch(`${server}/FtRangosDeFacturas/BySucursalUtiliza/1`);
        const data = await response.json();
        const rango = data.$values[0];
        const actual = parseInt(rango.numeroActual);
        const nuevo = actual + 1;
        const numero = `${rango.prefijo}-${String(nuevo).padStart(8, '0')}`;
        await fetch(`${server}/FtRangosDeFacturas/${rango.idRango}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...rango, numeroActual: nuevo, modificadoPor: user?.codigoUsuario || 'ADMIN' })
        });
        return numero;
    };



    // Funciones auxiliares para manejar la creación de facturas divididas
    const generarNumeroFactura = (prefijo, numero) => {
        return `${prefijo}-${String(numero).padStart(8, '0')}`;
    };

    const crearFacturaParaCliente = async ({
        clienteId,
        ordenTrabajo,
        valorFactura,
        rango,
        numeroFactura,
        descuento,
        user,
        datosExtra = {},
        server
    }) => {
        const facturaPayload = {
            idOrdenMedica: ordenTrabajo,
            idFormaPago: 1,
            fechaIngreso: new Date().toISOString(),
            idRango: rango.idRango,
            numeroFactura,
            puntosLacm: 0,
            lempirasLacm: 0,
            valorVenta: valorFactura,
            valorDescuento: descuento,
            ingresadoPor: user?.codigoUsuario || 'ADMIN',
            ...datosExtra
        };

        const response = await fetch(`${server}/FtFacturas`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(facturaPayload),
        });

        if (!response.ok) {
            throw new Error('Error al guardar la factura');
        }

        const data = await response.json();
        return data.idFactura;
    };



    useEffect(() => {

        const fetchTransacciones = async () => {
            try {
                const response = await fetch(`${server}/MgCajaTipoTransaccions`);
                const data = await response.json();
                const arr = data.$values || data || [];
                console.log(arr);

                const filtrado = arr.filter(item => item.mostrarEnCierreCaja === true);

                // Ordenar por el campo orden
                const ordenado = filtrado.sort((a, b) => (a.orden || 0) - (b.orden || 0));

                const procesado = ordenado.map(item => ({
                    ...item,
                    descripcionTransaccion: 'Total ' + item.descripcionTipoTransaccion,
                    valorInput: 0,
                    comisionCalculada: 0,
                    valorSistema: 0
                }));
                setTransaccionesCajero(procesado);
                // Se usa el mismo arreglo para la sección del sistema inicialmente
            } catch (error) {
                console.error('Error al cargar transacciones (cajero):', error);
            }
        };

        const fetchTipoDocumentos = async () => {
            try {
                const response = await fetch(`${server}/FtTiposDeDocumentosDePagos`);
                const data = await response.json();
                // Manejar tanto arrays directos como con $values
                const tiposArray = data.$values || data || [];
                setTiposDocumentos(tiposArray);
                console.log('Tipos de documentos cargados:', tiposArray);
            } catch (error) {
                console.error('Error al cargar tipos de documentos:', error);
            }
        };
        fetchOrigen();
        fetchTipoDocumentos();
        fetchTransacciones();

    }, []);

    useEffect(() => {
        if (!datosFactura || datosFactura.multiple) return;

        const recalcular = async () => {
            console.log('🔍 DIAGNÓSTICO datosFactura completo:', {
                valorVenta: datosFactura.valorVenta,
                valorDescuento: datosFactura.valorDescuento,
                TotalPagar: datosFactura.TotalPagar,
                valorPagar: datosFactura.valorPagar,
                ordenTrabajo: datosFactura.ordenTrabajo,
                todosLosCampos: Object.keys(datosFactura)
            });

            const valorVentaBruto = parseFloat(datosFactura.valorVenta ?? 0);
            const valorDescuento = parseFloat(datosFactura.valorDescuento ?? 0);
            const valorNetoCalculado = valorVentaBruto - valorDescuento;

            const totalRaw = parseFloat(
                datosFactura.valorVenta ??
                datosFactura.TotalPagar ??
                datosFactura.valorPagar ??
                0
            );
            const totalSeguro = Number.isFinite(totalRaw) ? totalRaw : 0;

            console.log('💰 CÁLCULOS base (desde datosFactura):', {
                valorVentaBruto,
                valorDescuento,
                valorNetoCalculado,
                totalSeguro
            });

            // ⚠️ NO usar valorVentaBruto/valorDescuento de datosFactura - puede estar desactualizado
            // Obtener SIEMPRE los detalles frescos de la API para calcular el total correcto
            try {
                const respDetalles = await fetch(`${server}/MgOrdenesMedicasDetalles/idOrdenTrabajo/${datosFactura.ordenTrabajo}`);
                if (respDetalles.ok) {
                    const dataDet = await respDetalles.json();
                    const detalles = dataDet.$values || dataDet || [];

                    // Calcular total REAL de la orden desde detalles (todos los análisis)
                    const valorVentaReal = detalles.reduce((sum, det) => sum + parseFloat(det.valorVenta || 0), 0);
                    const valorDescuentoReal = detalles.reduce((sum, det) => sum + parseFloat(det.valorDescuento || 0), 0);
                    const valorNetoReal = Math.max(valorVentaReal - valorDescuentoReal, 0);

                    console.log('🔍 TOTALES REALES desde detalles frescos:', {
                        totalDetalles: detalles.length,
                        valorVentaReal: valorVentaReal.toFixed(2),
                        valorDescuentoReal: valorDescuentoReal.toFixed(2),
                        valorNetoReal: valorNetoReal.toFixed(2)
                    });

                    // Calcular pendientes (solo análisis NO facturados)
                    const pendientes = detalles.filter(det => !det.idFactura || det.idFactura === 0);
                    const valorVentaPendiente = pendientes.reduce((sum, det) => sum + parseFloat(det.valorVenta || 0), 0);
                    const valorDescuentoPendiente = pendientes.reduce((sum, det) => sum + parseFloat(det.valorDescuento || 0), 0);
                    const netoPendiente = Math.max(valorVentaPendiente - valorDescuentoPendiente, 0);

                    console.log('🧮 Pendientes recalculados (detalles frescos):', {
                        pendientes: pendientes.length,
                        valorVentaPendiente: valorVentaPendiente.toFixed(2),
                        valorDescuentoPendiente: valorDescuentoPendiente.toFixed(2),
                        netoPendiente: netoPendiente.toFixed(2)
                    });

                    // Usar totales reales
                    setOriginalValorFactura(valorNetoReal);
                    setValorTotalOrden(netoPendiente);
                    setPendienteTotal(netoPendiente);
                    setMontoPendiente(netoPendiente);
                    setValorPagar(netoPendiente.toFixed(2));
                } else {
                    console.error('No se pudieron obtener detalles frescos de la orden para recalcular pendientes');
                    // Fallback a valores de datosFactura si falla la API
                    const valorNetoOrden = Math.max(valorVentaBruto - valorDescuento, 0);
                    setOriginalValorFactura(valorNetoOrden);
                    setValorTotalOrden(valorNetoOrden);
                    setPendienteTotal(valorNetoOrden);
                    setMontoPendiente(valorNetoOrden);
                    setValorPagar(valorNetoOrden.toFixed(2));
                }
            } catch (errFetch) {
                console.error('Error al recalcular pendientes con detalles frescos:', errFetch);
                // Fallback a valores de datosFactura si hay error
                const valorNetoOrden = Math.max(valorVentaBruto - valorDescuento, 0);
                setOriginalValorFactura(valorNetoOrden);
                setValorTotalOrden(valorNetoOrden);
                setPendienteTotal(valorNetoOrden);
                setMontoPendiente(valorNetoOrden);
                setValorPagar(valorNetoOrden.toFixed(2));
            }

            // Cargar facturas existentes y monto pendiente real
            try {
                const { montoPendiente: pendienteCalculado, pendienteNeto, totalFacturado, estado } =
                    await verificarEstadoFacturacion(datosFactura.ordenTrabajo, totalSeguro, valorDescuento);
                facturasCargadas.current = true;

                console.log('Factura>estado inicial', {
                    orden: datosFactura.ordenTrabajo,
                    totalSeguro,
                    valorDescuento,
                    pendienteCalculado,
                    pendienteNeto,
                    totalFacturado,
                    estado,
                });

                if (pendienteNeto !== null && pendienteNeto !== undefined) {
                    setPendienteTotal(pendienteNeto);
                    setMontoPendiente(pendienteNeto);
                    setValorPagar(parseFloat(pendienteNeto).toFixed(2));
                    console.log('✅ Valor a pagar actualizado a (neto):', parseFloat(pendienteNeto).toFixed(2));
                } else if (pendienteCalculado !== null && pendienteCalculado !== undefined) {
                    setPendienteTotal(pendienteCalculado);
                    setMontoPendiente(pendienteCalculado);
                    setValorPagar(parseFloat(pendienteCalculado).toFixed(2));
                    console.log('✅ Valor a pagar actualizado a (calculado):', parseFloat(pendienteCalculado).toFixed(2));
                }
            } catch (err) {
                console.error('Error al sincronizar facturación inicial:', err);
                setFacturas([]);
            }
        };

        recalcular();
    }, [datosFactura, server]);
    useEffect(() => {
        // Si aún no tenemos valor neto de la orden o no se han cargado facturas, no recalculamos
        if (!originalValorFactura || Number.isNaN(originalValorFactura) || !facturasCargadas.current) return;

        // Incluso si no hay facturas, calculamos el monto pendiente igual al valor original
        // IMPORTANTE: Sumar valores NETOS de las facturas (valorVenta - valorDescuento)
        const sumaUsados = facturas
            .filter(f => f && f.valorVenta != null)          // <-- filtramos nulos/undefined
            .reduce(
                (acc, f) => {
                    const valorNeto = parseFloat(f.valorVenta || 0) - parseFloat(f.valorDescuento || 0);
                    return acc + valorNeto;
                },
                0
            );

        // Calcular el valor NETO de la orden (bruto - descuento)
        // originalValorFactura ya es NETO; no restamos descuento nuevamente
        const valorOrdenNeto = parseFloat(originalValorFactura ?? 0);
        
        // Calcular pendiente NETO = Valor neto de orden - Suma de valores netos facturados
        const pendienteNeto = Math.max(valorOrdenNeto - sumaUsados, 0);

        console.log('📊 Recálculo pendiente por cambio en facturas:', {
            originalValorFactura: originalValorFactura.toFixed(2),
            valorDescuento: (parseFloat(datosFactura?.valorDescuento ?? 0)).toFixed(2),
            valorOrdenNeto: valorOrdenNeto.toFixed(2),
            sumaUsados: sumaUsados.toFixed(2),
            pendienteNeto: pendienteNeto.toFixed(2),
            facturasCount: facturas.length,
            // Información adicional para debugging
            datosFacturaValores: {
                valorVenta: datosFactura?.valorVenta,
                valorDescuento: datosFactura?.valorDescuento
            }
        });

        // Actualizamos tanto el valorPagar como el montoPendiente con el valor NETO
        setMontoPendiente(pendienteNeto);
        setPendienteTotal(pendienteNeto); // ✅ También actualizamos pendienteTotal para la validación
        setValorPagar(pendienteNeto.toFixed(2));

        // Actualizamos el estado de facturación
        if (Math.abs(sumaUsados - originalValorFactura) < 0.01) { // Tolerancia para errores de decimales
            setEstadoFacturacion('facturado-completo');
        } else if (sumaUsados > 0) {
            setEstadoFacturacion('facturado-parcial');
        } else {
            setEstadoFacturacion('sin-facturar');
        }
    }, [facturas, originalValorFactura, datosFactura]);
    
    // Cargar recibos y notas de crédito cuando cambie la factura seleccionada
    useEffect(() => {
        if (facturas && facturas.length > 0) {
            fetchRecibos(facturas[0].idFactura);
            fetchNotasCredito(facturas[0].idFactura);
        }
    }, [facturas]);



    const handleAgregarDocumento = () => {
        if (!tipoDocumento || !valorPagado) {
            alert('Por favor complete el tipo de documento y el valor pagado.');
            return;
        }

        if (valorPagado <= 0) {
            alert('El valor pagado no puede ser menor o igual a 0.');
            return;
        }

        // Primero, obtén la descripción del tipo de documento
        const descripcion = transaccionesCajero.find(
            (doc) => doc.id === tipoDocumento
        )?.descripcionTipoTransaccion;

        // Ahora puedes usar la variable 'descripcion' en la condición
        if (
            descripcion &&
            descripcion.toLowerCase().includes("puntos") &&
            parseFloat(valorPagado) > parseFloat(lempirasActuales)
        ) {
            alert('El valor pagado no puede ser mayor que el saldo de lempiras actuales.');
            return;
        }

        console.log('AQUI ESTA LA DESCRIPCION ', descripcion);

        let valorPuntos = 0;
        // Si la descripción contiene "puntos", se ajusta el saldo de lempiras
        if (descripcion && descripcion.toLowerCase().includes("puntos")) {
            valorPuntos = parseFloat(lempirasActuales) - parseFloat(valorPagado);
            setLempirasActuales(valorPuntos.toFixed(2));
        }

        const nuevoDocumento = {
            descripcion,
            tipoDocumento,
            numero, // Asegúrate de que "numero" esté definido en tu componente
            valorPagado: parseFloat(valorPagado),
        };

        setDocumentos([...documentos, nuevoDocumento]);

        // Resetear campos
        setTipoDocumento('');
        setNumero('');
        setValorPagado('');
    };

    const handleEliminarDocumento = (index) => {
        const documentoEliminado = documentos[index];

        // Si el tipo de documento es 17, sumar el valorPagado a lempirasActuales
        if (
            documentoEliminado.descripcion &&
            documentoEliminado.descripcion.toLowerCase().includes('puntos')
        ) {
            setLempirasActuales((prevLempiras) => parseFloat(prevLempiras) + parseFloat(documentoEliminado.valorPagado));
        }

        // Eliminar el documento del arreglo
        const nuevosDocumentos = documentos.filter((_, i) => i !== index);
        setDocumentos(nuevosDocumentos);
    };

    const recalcularCambio = () => {
        const totalValorPagado = documentos.reduce((acc, doc) => acc + doc.valorPagado, 0);
        const valorPagarNumber = parseFloat(valorPagar) || 0;
        const cambioCalculado = totalValorPagado - valorPagarNumber;
        setCambio(cambioCalculado);
    };

    useEffect(() => {
        recalcularCambio();

    }, [documentos, valorPagar]);

    // ref para que sólo se dispare una vez
    const yaMostroAlerta = useRef(false);
    // ref para saber si ya cargamos facturas desde el backend
    const facturasCargadas = useRef(false);

    // useEffect deshabilitado - advertencia removida por solicitud del usuario
    // useEffect(() => {
    //     // si ya salió la alerta o no tenemos pendiente, salimos
    //     if (yaMostroAlerta.current || pendienteTotal == null) return;

    //     const montoTotal = dividirFactura
    //         ? montoClientePrincipal + montoClienteSecundario
    //         : parseFloat(valorPagar);

    //     if (montoTotal > pendienteTotal + 0.01) {
    //         yaMostroAlerta.current = true;
    //         Swal.fire({
    //             icon: 'warning',
    //             title: 'Advertencia',
    //             text: `El monto a facturar (${montoTotal.toFixed(2)}) excede el pendiente (${pendienteTotal.toFixed(2)})`,
    //         });
    //         // ajustamos sólo una vez
    //         setValorPagar(pendienteTotal.toFixed(2));
    //         if (dividirFactura) {
    //             const mitad = (pendienteTotal / 2).toFixed(2);
    //             setMontoClientePrincipal(mitad);
    //             setMontoClienteSecundario(mitad);


    //         }
    //     }
    // }, [pendienteTotal, dividirFactura]);


    const handleImprimirCarta = async (idFactura) => {
        try {
            ///api/MgOrdenesMedicas/ImprimirOrdenMedica/{idOrden}
            const response = await fetch(`${server}/FtFacturas/ImprimirFactura/${idFactura}`, {
                method: 'POST',
            });
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const newWindow = window.open(url, '_blank');
                if (newWindow) {
                    newWindow.focus();
                } else {
                    alert('Por favor, permite las ventanas emergentes para previsualizar el documento.');
                }
            } else {
                const errorData = await response.json();
                const errorMessage = errorData.message || (errorData.errors ? Object.values(errorData.errors).flat().join(', ') : 'Ocurrió un error inesperado');
                Swal.fire({
                    icon: 'error',
                    title: `Error ${response.status}`,
                    text: errorMessage,
                });
            }
        } catch (error) {

            Swal.fire({
                icon: 'error',
                title: `Error ${error}`,
                text: error,
            });
        }
    };

    const handleImprimirTicket = async (idFactura) => {
        try {
            ///api/MgOrdenesMedicas/ImprimirOrdenMedica/{idOrden}
            const response = await fetch(`${server}/FtFacturas/ImprimirFacturaticket/${idFactura}`, {
                method: 'POST',
            });
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const newWindow = window.open(url, '_blank');
                if (newWindow) {
                    newWindow.focus();
                } else {
                    alert('Por favor, permite las ventanas emergentes para previsualizar el documento.');
                }
            } else {
                const errorData = await response.json();
                const errorMessage = errorData.message || (errorData.errors ? Object.values(errorData.errors).flat().join(', ') : 'Ocurrió un error inesperado');
                Swal.fire({
                    icon: 'error',
                    title: `Error ${response.status}`,
                    text: errorMessage,
                });
            }
        } catch (error) {

            Swal.fire({
                icon: 'error',
                title: `Error ${error}`,
                text: error,
            });
        }
    };

    const fetchPuntosLempiras = async (clienteId) => {
        try {
            const response = await fetch(`${server}/FtFacturas/PuntosDisponibles/${clienteId}`);
            if (response.ok) {
                const data = await response.json();
                if (data) {
                    setPuntosActuales(data.puntos || 0);
                    setLempirasActuales(data.lempiras || 0);
                } else {
                    setPuntosActuales(0);
                    setLempirasActuales(0);
                }
            }
        } catch (error) {
            console.error("Error al obtener puntos:", error);
            setPuntosActuales(0);
            setLempirasActuales(0);
        }
    };
    
    // Método para obtener los recibos asociados a una factura
    const fetchRecibos = async (idFactura) => {
        if (!idFactura) return;
        
        try {
            const response = await fetch(`${server}/MgRecibos/PorFactura/${idFactura}`);
            
            if (response.ok) {
                const data = await response.json();
                console.log('Datos de recibos recibidos:', data);
                
                // Deserializar la estructura con $values
                let recibosArray = [];
                if (data && data.$values && Array.isArray(data.$values)) {
                    recibosArray = data.$values;
                } else if (Array.isArray(data)) {
                    recibosArray = data;
                }
                
                console.log('📋 Recibos array antes de procesar:', recibosArray);
                console.log('📋 Cantidad de recibos:', recibosArray.length);
                
                // Cargar los detalles de cada recibo
                const recibosConDetalles = await Promise.all(
                    recibosArray.map(async (recibo, idx) => {
                        try {
                            console.log(`📄 Procesando recibo ${idx + 1}:`, recibo);
                            console.log(`📄 Propiedades disponibles:`, Object.keys(recibo));
                            
                            const idRecibo = recibo.IdRecibo || 
                                           recibo.idRecibo || 
                                           recibo.Id || 
                                           recibo.id;
                            
                            console.log(`📄 ID extraído del recibo ${idx + 1}:`, idRecibo);
                            
                            if (!idRecibo || idRecibo === 0) {
                                console.error(`❌ Recibo ${idx + 1} no tiene un ID válido:`, recibo);
                                return recibo; // Devolver el recibo tal como está para debugging
                            }
                            
                            const detallesResponse = await fetch(`${server}/MgRecibos/${idRecibo}`);
                            
                            if (detallesResponse.ok) {
                                const reciboCompleto = await detallesResponse.json();
                                console.log(`✅ Recibo completo obtenido para ID ${idRecibo}:`, reciboCompleto);
                                
                                // Deserializar detalles del recibo completo
                                const detalles = reciboCompleto.MgRecibosDetalles?.$values || 
                                               reciboCompleto.mgRecibosDetalles?.$values || 
                                               reciboCompleto.MgRecibosDetalles || 
                                               reciboCompleto.mgRecibosDetalles || 
                                               [];
                                
                                // Asegurar que todas las propiedades importantes estén disponibles
                                const reciboNormalizado = {
                                    // Mantener todas las propiedades originales
                                    ...recibo,
                                    // Sobrescribir con las del recibo completo si existen
                                    ...reciboCompleto,
                                    // Asegurar ID en todas las variantes
                                    IdRecibo: reciboCompleto.IdRecibo || reciboCompleto.idRecibo || recibo.IdRecibo || recibo.idRecibo || idRecibo,
                                    idRecibo: reciboCompleto.idRecibo || reciboCompleto.IdRecibo || recibo.idRecibo || recibo.IdRecibo || idRecibo,
                                    // Detalles
                                    MgRecibosDetalles: detalles,
                                    mgRecibosDetalles: detalles
                                };
                                
                                console.log(`✅ Recibo normalizado:`, reciboNormalizado);
                                return reciboNormalizado;
                            }
                            
                            console.warn(`⚠️ No se pudieron obtener detalles para recibo ${idRecibo}`);
                            // Si falla, retornar el recibo con el ID asegurado
                            return {
                                ...recibo,
                                IdRecibo: idRecibo,
                                idRecibo: idRecibo,
                                MgRecibosDetalles: [],
                                mgRecibosDetalles: []
                            };
                        } catch (error) {
                            const idRecibo = recibo.IdRecibo || 
                                           recibo.idRecibo || 
                                           recibo.Id || 
                                           recibo.id;
                            console.error(`❌ Error al cargar detalles del recibo ${idRecibo}:`, error);
                            return {
                                ...recibo,
                                IdRecibo: idRecibo,
                                idRecibo: idRecibo,
                                MgRecibosDetalles: [],
                                mgRecibosDetalles: []
                            };
                        }
                    })
                );
                
                console.log('Recibos procesados con detalles:', recibosConDetalles);
                setRecibos(recibosConDetalles);
            } else if (response.status === 404) {
                // Si no se encuentran recibos (404), establecer como arreglo vacío
                console.log('No se encontraron recibos para la factura');
                setRecibos([]);
            } else {
                console.error(`Error al obtener recibos: ${response.status}`);
                setRecibos([]);
            }
        } catch (error) {
            console.error("Error al obtener los recibos:", error);
            setRecibos([]);
        }
    };
    
    // Método para obtener los detalles de pago de una factura desde MgCajaArqueoDetalleSistemas
    const fetchDetallesPago = async (idFactura) => {
        if (!idFactura) return;
        
        setLoadingDetallesPago(true);
        try {
            const response = await fetch(`${server}/MgCajaArqueoDetalleSistemas/porFactura/${idFactura}`);
            
            if (response.ok) {
                const data = await response.json();
                console.log('Detalles de pago recibidos:', data);
                
                // Deserializar la estructura con $values si existe
                let detallesArray = [];
                if (data && data.$values && Array.isArray(data.$values)) {
                    detallesArray = data.$values;
                } else if (Array.isArray(data)) {
                    detallesArray = data;
                }
                
                // Procesar cada detalle adaptando los nombres de propiedades del endpoint
                const detallesProcessed = detallesArray.map(detalle => ({
                    ...detalle,
                    // Mapeo de propiedades del endpoint MgCajaArqueoDetalleSistemas
                    Id: detalle.id || detalle.Id,
                    IdTipoTransaccion: detalle.idTipoTransaccion || detalle.IdTipoTransaccion,
                    DescripcionTipoTransaccion: detalle.descripcionTipoTransaccion || detalle.DescripcionTipoTransaccion,
                    Valor: detalle.valor || detalle.Valor,
                    NumeroDocumento: detalle.numeroDocumento || detalle.NumeroDocumento,
                    IngresadoPor: detalle.ingresadoPor || detalle.IngresadoPor,
                    FechaIngreso: detalle.fechaIngreso || detalle.FechaIngreso,
                    FechaTransaccion: detalle.fechaTransaccion || detalle.FechaTransaccion
                }));
                
                console.log('Detalles de pago procesados:', detallesProcessed);
                setDetallesPago(detallesProcessed);
            } else if (response.status === 404) {
                console.log('No se encontraron detalles de pago para la factura');
                setDetallesPago([]);
            } else {
                console.error(`Error al obtener detalles de pago: ${response.status}`);
                setDetallesPago([]);
            }
        } catch (error) {
            console.error("Error al obtener los detalles de pago:", error);
            setDetallesPago([]);
        } finally {
            setLoadingDetallesPago(false);
        }
    };
    
    // Método para abrir el modal de detalles de pago
    const handleVerDetallesPago = (factura) => {
        setFacturaDetallesPago(factura);
        setOpenDetallesPago(true);
        setEditandoDetallesPago(false);
        fetchDetallesPago(factura.idFactura);
    };
    
    // Método para habilitar edición de detalles de pago
    const handleEditarDetallesPago = () => {
        console.log('🔧 Iniciando modo edición de detalles de pago');
        console.log('📋 Transacciones disponibles:', transaccionesCajero);
        console.log('💰 Detalles de pago actuales:', detallesPago);
        
        if (!transaccionesCajero || transaccionesCajero.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Tipos de documentos no cargados',
                text: 'Los tipos de documentos no están disponibles. Refresque la página e intente nuevamente.',
            });
            return;
        }
        
        setDetallesPagoEditados(JSON.parse(JSON.stringify(detallesPago)));
        setEditandoDetallesPago(true);
    };
    
    // Método para cancelar edición
    const handleCancelarEdicionDetalles = () => {
        setEditandoDetallesPago(false);
        setDetallesPagoEditados([]);
    };
    
    // Método para actualizar un detalle de pago editado
    const handleActualizarDetallePago = (index, campo, valor) => {
        const nuevosDetalles = [...detallesPagoEditados];
        nuevosDetalles[index] = {
            ...nuevosDetalles[index],
            [campo]: valor
        };
        setDetallesPagoEditados(nuevosDetalles);
    };
    
    // Método para validar que el total se mantenga
    const validarTotalDetallesPago = () => {
        // Calcular el valor a pagar de la factura (valor venta - descuento)
        const valorVentaFactura = parseFloat(facturaDetallesPago?.valorVenta || 0);
        const valorDescuentoFactura = parseFloat(facturaDetallesPago?.valorDescuento || 0);
        const valorAPagarFactura = valorVentaFactura - valorDescuentoFactura;
        
        const totalEditado = detallesPagoEditados.reduce((sum, det) => 
            sum + parseFloat(det.Valor || det.valor || 0), 0
        );
        
        // Comparar el total editado con el valor a pagar de la factura (con descuento restado)
        return Math.abs(valorAPagarFactura - totalEditado) < 0.01;
    };
    
    // Método para guardar cambios en detalles de pago
    const handleGuardarDetallesPago = async () => {
        // Validar totales solo si no está permitida la modificación libre
        if (!PERMITIR_MODIFICACION_LIBRE_MONTOS && !validarTotalDetallesPago()) {
            Swal.fire({
                icon: 'error',
                title: 'Error de validación',
                text: 'El total de los pagos debe mantenerse igual. No puede cambiar valores sin moverlos a otro método de pago.',
                footer: 'Ajuste los montos para que cuadren correctamente'
            });
            return;
        }
        
        try {
            // Mostrar advertencia sobre partida automática
            const resultado = await Swal.fire({
                icon: 'warning',
                title: '⚠️ Advertencia Importante',
                html: `
                    <p><strong>Al modificar los métodos de pago de esta factura:</strong></p>
                    <ul style="text-align: left; margin: 10px 40px;">
                        <li>Se actualizarán los movimientos de caja</li>
                        <li>Deberá volver a ejecutar la <strong>Partida Automática de Cierre Diario</strong></li>
                        <li>Esto afectará la contabilización del día de esta factura</li>
                    </ul>
                    <p>¿Está seguro de continuar?</p>
                `,
                showCancelButton: true,
                confirmButtonText: 'Sí, guardar cambios',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6'
            });
            
            if (!resultado.isConfirmed) {
                return;
            }
            
            // Guardar cada detalle modificado
            let errores = 0;
            for (const detalle of detallesPagoEditados) {
                const detalleId = detalle.Id || detalle.id;
                const payload = {
                    id: detalleId,
                    idTipoTransaccion: parseInt(detalle.IdTipoTransaccion || detalle.idTipoTransaccion),
                    valor: parseFloat(detalle.Valor || detalle.valor),
                    numeroDocumento: detalle.NumeroDocumento || detalle.numeroDocumento,
                    ingresadoPor: detalle.IngresadoPor || detalle.ingresadoPor,
                    idEncabezado: detalle.IdEncabezado || detalle.idEncabezado,
                    idFactura: detalle.IdFactura || detalle.idFactura,
                    modificadoPor: user?.codigoUsuario || user?.nombreUsuario || 'ADMIN'
                };
                
                try {
                    const response = await fetch(`${server}/MgCajaArqueoDetalleSistemas/${detalleId}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload)
                    });
                    
                    if (!response.ok) {
                        console.error(`Error al actualizar detalle ${detalleId}`);
                        errores++;
                    }
                } catch (error) {
                    console.error(`Error al actualizar detalle ${detalleId}:`, error);
                    errores++;
                }
            }
            
            if (errores === 0) {
                await Swal.fire({
                    icon: 'success',
                    title: 'Cambios guardados',
                    html: `
                        <p>Los métodos de pago se han actualizado correctamente.</p>
                        <p><strong>📋 Recordatorio:</strong> Debe ejecutar la <strong>Partida Automática de Cierre Diario</strong> nuevamente.</p>
                    `,
                    confirmButtonText: 'Entendido'
                });
                
                // Recargar detalles
                await fetchDetallesPago(facturaDetallesPago.idFactura);
                setEditandoDetallesPago(false);
                setDetallesPagoEditados([]);
            } else {
                Swal.fire({
                    icon: 'warning',
                    title: 'Guardado con errores',
                    text: `Se guardaron los cambios pero ${errores} detalle(s) tuvieron error.`,
                });
            }
            
        } catch (error) {
            console.error('Error al guardar detalles de pago:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudieron guardar los cambios en los detalles de pago'
            });
        }
    };
    
    // Método para imprimir un recibo
    const handleImprimirRecibo = async (idRecibo) => {
        console.log('ID de recibo a imprimir:', idRecibo);
        
        if (!idRecibo || idRecibo === 0) {
            console.error('ID de recibo inválido:', idRecibo);
            Swal.fire('Error', 'ID de recibo inválido', 'error');
            return;
        }
        
        try {
            const response = await fetch(`${server}/MgRecibos/ImprimirRecibo/${idRecibo}`, {
                method: 'POST'
            });
            
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                
                // Abrir PDF en nueva ventana
                const newWindow = window.open(url, '_blank');
                if (newWindow) {
                    newWindow.focus();
                } else {
                    alert('Por favor, permite las ventanas emergentes para ver el recibo.');
                }
            } else {
                console.error(`Error al generar el PDF del recibo: ${response.status}`);
                Swal.fire('Error', 'No se pudo generar el PDF del recibo', 'error');
            }
        } catch (error) {
            console.error("Error al imprimir el recibo:", error);
            Swal.fire('Error', 'No se pudo imprimir el recibo', 'error');
        }
    };
    
    // Método para crear un nuevo recibo
    const handleCrearRecibo = async () => {
        if (!idCliente) {
            Swal.fire('Error', 'Debe seleccionar un cliente', 'error');
            return;
        }
        
        // Validar que haya al menos un detalle
        if (!nuevoReciboData.detalles || nuevoReciboData.detalles.length === 0) {
            Swal.fire('Error', 'Debe agregar al menos un detalle de abono', 'error');
            return;
        }
        
        // Validar que el valor del recibo sea mayor a 0
        if (toCents(nuevoReciboData.valorRecibo || 0) <= 0) {
            Swal.fire('Error', 'El valor del recibo debe ser mayor a cero', 'error');
            return;
        }
        
        // Validar documentos de pago
        const validacion = validarDocumentosPago();
        if (!validacion.valido) {
            Swal.fire('Error', validacion.mensaje, 'error');
            return;
        }
        
        // Validar que haya documentos de pago si el valor es mayor a 0
        if (toCents(nuevoReciboData.valorRecibo || 0) > 0 && 
            (!nuevoReciboData.documentosPago || nuevoReciboData.documentosPago.length === 0)) {
            Swal.fire('Error', 'Debe agregar al menos un documento de pago', 'error');
            return;
        }
        
        try {
            // Preparar los datos del recibo según la estructura del modelo MgRecibo del controlador
            const ahora = new Date();
            const fechaLocal = new Date(ahora.getTime() - (ahora.getTimezoneOffset() * 60000));
            
            const reciboData = {
                IdCliente: parseInt(idCliente),
                FechaRecibo: fechaLocal.toISOString().slice(0, 19), // Formato: YYYY-MM-DDTHH:mm:ss (sin 'Z')
                Anio: ahora.getFullYear(),
                Mes: ahora.getMonth() + 1,
                SaldoAnterior: roundAmount(nuevoReciboData.saldoAnterior),
                ValorRecibo: roundAmount(nuevoReciboData.valorRecibo),
                SaldoActual: roundAmount((nuevoReciboData.saldoAnterior || 0) - (nuevoReciboData.valorRecibo || 0)),
                Concepto: nuevoReciboData.concepto || 'Abono a factura',
                PuntosLacm: parseFloat(puntosActuales) || 0,
                LempirasLacm: parseFloat(lempirasActuales) || 0,
                IngresadoPor: user?.nombreUsuario || user?.codigoUsuario || 'ADMIN',
                // Detalles del recibo (relación con facturas)
                MgRecibosDetalles: (nuevoReciboData.detalles || []).map(det => ({
                    IdFactura: parseInt(det.idFactura),
                    SaldoInicial: roundAmount(det.saldoInicial),
                    Abono: roundAmount(det.abono),
                    SaldoFinal: roundAmount(det.saldoFinal || (det.saldoInicial || 0) - (det.abono || 0)),
                    IngresadoPor: user?.nombreUsuario || user?.codigoUsuario || 'ADMIN'
                }))
            };
            
            console.log('Enviando datos del recibo:', reciboData);
            
            // Usar el endpoint POST del controlador MgRecibos
            const response = await fetch(`${server}/MgRecibos/${cajaAbiertaDatos ?? ''}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(reciboData)
            });
            
            console.log('Respuesta del servidor:', response.status, response.statusText);
            
            if (response.ok) {
                const reciboCreado = await response.json();
                console.log('Recibo creado - Respuesta completa:', reciboCreado);
                console.log('Recibo creado - Propiedades:', Object.keys(reciboCreado));
                
                // Intentar obtener el ID del recibo de diferentes formas
                const idRecibo = reciboCreado.IdRecibo || 
                                reciboCreado.idRecibo || 
                                reciboCreado.Id || 
                                reciboCreado.id ||
                                reciboCreado.ID;
                
                console.log('ID del recibo obtenido:', idRecibo);
                
                if (!idRecibo || idRecibo === 0) {
                    console.error('⚠️ ERROR: No se pudo obtener el ID del recibo creado');
                    console.error('Objeto recibido del servidor:', JSON.stringify(reciboCreado, null, 2));
                    Swal.fire({
                        icon: 'warning',
                        title: 'Recibo creado con advertencia',
                        text: 'El recibo se creó pero no se pudieron guardar los movimientos de caja. Verifique con el administrador.',
                    });
                    return;
                }
                
                // Después de crear el recibo exitosamente, crear los detalles de pago para las facturas
                const documentosPagoRecibo = nuevoReciboData.documentosPago || [];
                const facturasAfectadas = nuevoReciboData.detalles || [];
                
                try {
                    // Validar que la caja esté abierta antes de guardar movimientos
                    if (!cajaAbiertaDatos) {
                        console.warn('No hay caja abierta, no se guardarán movimientos de caja para el recibo');
                    }
                    
                    // Obtener el número de recibo para usarlo como documento
                    const numeroRecibo = `C${String(idRecibo).padStart(9, '0')}`;
                    
                    // Guardar cada documento de pago en MgCajaArqueoDetalleSistemas
                    // Si el recibo tiene múltiples facturas, usar la primera como referencia principal
                    const idFacturaPrincipal = facturasAfectadas.length > 0 ? facturasAfectadas[0].idFactura : null;
                    
                    for (const documento of documentosPagoRecibo) {
                        const valorDocumento = parseFloat(documento.valorPagado) || 0;
                        
                        if (valorDocumento > 0 && cajaAbiertaDatos) {
                            const payloadCaja = {
                                idTipoTransaccion: parseInt(documento.tipoDocumento),
                                valor: valorDocumento,
                                numeroDocumento: numeroRecibo,
                                concepto: `Abono a recibo ${numeroRecibo}`,
                                ingresadoPor: user?.codigoUsuario || user?.nombreUsuario || 'ADMIN',
                                idEncabezado: cajaAbiertaDatos,
                                idFactura: idFacturaPrincipal
                            };
                            
                            console.log('Guardando movimiento de caja para recibo:', payloadCaja);
                            
                            try {
                                const responseCaja = await fetch(`${server}/MgCajaArqueoDetalleSistemas`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify(payloadCaja)
                                });
                                
                                if (responseCaja.ok) {
                                    console.log('Movimiento de caja guardado exitosamente para recibo');
                                } else {
                                    console.error('Error al guardar movimiento de caja para recibo:', responseCaja.statusText);
                                }
                            } catch (errorCaja) {
                                console.error('Error al guardar movimiento de caja para recibo:', errorCaja);
                            }
                            
                            // Si el documento es de puntos, crear el registro adicional
                            if (documento.descripcion && documento.descripcion.toLowerCase().includes('puntos')) {
                                try {
                                    const fechaActual = new Date();
                                    const dataPuntos = {
                                        fecha: fechaActual.toISOString().split('T')[0],
                                        idTipo: 1,
                                        idComercio: 1,
                                        documento: numeroRecibo,
                                        ingresadoPor: user?.codigoUsuario || user?.nombreUsuario || 'ADMIN',
                                    };
                                    
                                    console.log('Puntos Cobro a guardar para recibo:', dataPuntos);
                                    const responsePuntos = await fetch(`${server}/MgClientesPuntosCobroes`, {
                                        method: 'POST',
                                        headers: {
                                            'Content-Type': 'application/json',
                                        },
                                        body: JSON.stringify(dataPuntos),
                                    });
                                    
                                    if (responsePuntos.ok) {
                                        const puntosData = await responsePuntos.json();
                                        const idCobro = puntosData.id || puntosData.Id;
                                        const puntos = valorDocumento / 0.30; // Conversión estándar
                                        
                                        const dataPuntosDetalle = {
                                            idCobro: idCobro,
                                            idCliente: parseInt(idCliente),
                                            valorPuntos: puntos,
                                            valorLempiras: valorDocumento,
                                            ingresadoPor: user?.codigoUsuario || user?.nombreUsuario || 'ADMIN',
                                        };
                                        
                                        await fetch(`${server}/MgClientesPuntosCobrosDetalles`, {
                                            method: 'POST',
                                            headers: {
                                                'Content-Type': 'application/json',
                                            },
                                            body: JSON.stringify(dataPuntosDetalle),
                                        });
                                    }
                                } catch (errorPuntos) {
                                    console.error('Error al procesar puntos para recibo:', errorPuntos);
                                }
                            }
                        }
                    }
                    
                    console.log('Movimientos de caja guardados correctamente para el recibo');
                } catch (error) {
                    console.error('Error al guardar movimientos de caja para el recibo:', error);
                    // No mostrar error al usuario ya que el recibo se creó correctamente
                    // Solo registrar el error en consola
                }
                
                Swal.fire({
                    title: 'Recibo creado',
                    text: `El recibo C${String(idRecibo).padStart(9, '0')} se ha creado correctamente`,
                    icon: 'success',
                    showCancelButton: true,
                    confirmButtonText: 'Imprimir Recibo',
                    cancelButtonText: 'Cerrar'
                }).then((result) => {
                    if (result.isConfirmed) {
                        handleImprimirRecibo(idRecibo);
                    }
                    // Actualizar la lista de recibos para todas las facturas
                    if (facturas && facturas.length > 0) {
                        // Recargar recibos para cada factura que tenga detalles en este recibo
                        const facturasAfectadas = nuevoReciboData.detalles.map(det => det.idFactura);
                        facturasAfectadas.forEach(idFactura => {
                            fetchRecibos(idFactura);
                        });
                        // También recargar para la primera factura si no hay detalles específicos
                        if (facturasAfectadas.length === 0) {
                            fetchRecibos(facturas[0].idFactura);
                        }
                    }
                    // Cerrar el modal y limpiar datos
                    setOpenNuevoRecibo(false);
                    setNuevoReciboData({
                        idCliente: '',
                        valorRecibo: 0,
                        saldoAnterior: 0,
                        saldoActual: 0,
                        concepto: '',
                        detalles: [],
                        documentosPago: []
                    });
                });
            } else {
                // Manejo mejorado de errores
                let errorMessage = 'Error desconocido';
                try {
                    const errorData = await response.json();
                    console.error('Detalles del error:', errorData);
                    errorMessage = errorData.message || errorData.Message || JSON.stringify(errorData);
                } catch (e) {
                    console.error('Error al parsear respuesta de error:', e);
                    errorMessage = `Error ${response.status}: ${response.statusText}`;
                }
                console.error('Error response:', errorMessage);
                Swal.fire('Error', `No se pudo crear el recibo: ${errorMessage}`, 'error');
            }
        } catch (error) {
            console.error("Error al crear el recibo:", error);
            Swal.fire('Error', `No se pudo crear el recibo: ${error.message}`, 'error');
        }
    };

    const verificarEstadoFacturacion = async (idOrden, valorTotal, valorDescuento = 0) => {
        try {
            const valorTotalNum = parseFloat(valorTotal) || 0;
            const descuentoNum = parseFloat(valorDescuento) || 0;
            
            console.log(`📊 Verificando facturación para orden: ${idOrden}`, {
                valorTotal: valorTotalNum,
                valorDescuento: descuentoNum,
                pendienteEsperado: valorTotalNum - descuentoNum
            });

            // Si no hay idOrden, no podemos continuar
            if (!idOrden) {
                console.log("No hay ID de orden para verificar facturación");
                setEstadoFacturacion('sin-facturar');
                setMontoPendiente(valorTotalNum);
                return { estado: 'sin-facturar', montoPendiente: valorTotalNum, totalFacturado: 0 };
            }

            const response = await fetch(`${server}/FtFacturas/facturasbyorden/${idOrden}`);

            if (!response.ok) {
                console.log(`Error en respuesta de API: ${response.status}`);
                setEstadoFacturacion('sin-facturar');
                setMontoPendiente(valorTotalNum);
                return { estado: 'sin-facturar', montoPendiente: valorTotalNum, totalFacturado: 0 };
            }

            const data = await response.json();


            const facturasOrden = Array.isArray(data) ? data : (data.$values || []);
            setFacturas(facturasOrden);
            console.log(`Facturas encontradas: ${facturasOrden.length}`);



            if (facturasOrden.length === 0) {
                console.log("No hay facturas, pendiente igual al total");
                setEstadoFacturacion('sin-facturar');
                setMontoPendiente(valorTotalNum);
                return { estado: 'sin-facturar', montoPendiente: valorTotalNum, totalFacturado: 0 };
            }

            // Sumar el total NETO de todas las facturas (valor - descuento)
            const totalFacturadoNeto = facturasOrden.reduce((sum, factura) => {
                const valorFactura = parseFloat(factura.valorVenta) || 0;
                const descuentoFactura = parseFloat(factura.valorDescuento) || 0;
                const valorNeto = valorFactura - descuentoFactura;
                console.log(`🧾 Factura: ${factura.numeroFactura}, valor bruto: ${valorFactura}, descuento: ${descuentoFactura}, neto: ${valorNeto}`);
                return sum + valorNeto;
            }, 0);

            // Calcular el valor NETO de la orden (bruto - descuento)
            const valorOrdenNeto = valorTotalNum - descuentoNum;

            console.log(`💵 Total facturado (neto): ${totalFacturadoNeto.toFixed(2)}, Valor total orden (neto): ${valorOrdenNeto.toFixed(2)}`);
            
            // Calcular el monto pendiente NETO
            const pendienteNeto = Math.max(valorOrdenNeto - totalFacturadoNeto, 0);

            console.log(`📈 Pendiente calculado (neto): ${pendienteNeto.toFixed(2)}`);
            
            // Determinar el estado de facturación comparando valores NETOS
            let estado;
            if (Math.abs(totalFacturadoNeto - valorOrdenNeto) < 0.01) { // Tolerancia para decimales
                estado = 'facturado-completo';
            } else if (totalFacturadoNeto > 0) {
                estado = 'facturado-parcial';
            } else {
                estado = 'sin-facturar';
            }
            console.log(`Estado de facturación: ${estado}`);
            setEstadoFacturacion(estado);
            setMontoPendiente(pendienteNeto);
            return {
                estado,
                montoPendiente: pendienteNeto,
                pendienteNeto: pendienteNeto,
                totalFacturado: totalFacturadoNeto
            };

        } catch (error) {
            console.error('Error al verificar facturación:', error);
            setEstadoFacturacion('sin-facturar');
            setMontoPendiente(parseFloat(valorTotal) || 0);
            return { estado: 'sin-facturar', montoPendiente: parseFloat(valorTotal) || 0, pendienteNeto: parseFloat(valorTotal) || 0, totalFacturado: 0 };
        }
    };
    // En tu componente padre:
    const handleDespachar = async (isSecondary = false) => {

        console.log(idCliente);
        // return;
        if (despachando) return;              // evita dobles clicks
        // Elegimos los estados según principal/secundario:
        const montoAFacturar = dividirFactura
            ? (isSecondary
                ? parseFloat(montoClienteSecundario)
                : parseFloat(montoClientePrincipal))
            : parseFloat(valorPagar);

        const puntosAFacturar = dividirFactura
            ? (isSecondary
                ? puntosActualesSec
                : puntosActuales)
            : puntosActuales;

        const lempirasAFacturar = dividirFactura
            ? (isSecondary
                ? lempirasSecundario
                : lempirasActuales)
            : lempirasActuales;

        const docsAFacturar = dividirFactura
            ? (isSecondary
                ? (docSecundario || [])
                : (docPrincipal || []))
            : (documentos || []);

        const cambioLocal = dividirFactura ? (isSecondary ? cambioSecundario : cambioPrincipal) : cambio;
        const montoConfirmadoLocal = dividirFactura
            ? (isSecondary ? montoConfirmadoSec : montoConfirmadoPr)
            : montoConfirmado;               //  <-- aquí el cambio clave

        console.log(isSecondary);
        console.log(montoConfirmadoLocal);
        console.log(documentos);
        console.log(docPrincipal, docSecundario);

        // Origen de la factura
        const idOrigenValor = clienteOrigenChecked && origen ? origen.idOrigen : null;

        // Sucursal activa
        let sucursalActiva = idSucursal;
        if (!sucursalActiva && ordenesSeleccionadas.length) {
            sucursalActiva = ordenesSeleccionadas[0].idSucursal;
            setIdSucursal(sucursalActiva);
        }
        if (!sucursalActiva) {
            Swal.fire('Error', 'No se encontró la sucursal para despachar la factura.');
            return;
        }

        // Validar caja abierta
        const operacionValida = await validarOperacionCritica('facturación');
        if (!operacionValida) return;

        // Validaciones básicas
        if (cambioLocal < 0 && tipoFactura !== 'credito') {
            alert('No ha completado el pago de la factura.');
            return;
        }

        if (cambioLocal > 0 && tipoFactura !== 'credito'){
            alert('El total de los métodos de pago es mayor al total de la factura.');
            return;
        }

        if (!montoConfirmadoLocal && tipoFactura !== 'credito') {
            alert('Tienen que confirmar el monto a pagar.');
            return;
        }

        if (!montoAFacturar || parseFloat(montoAFacturar) <= 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Monto inválido',
                text: 'El valor a pagar debe ser mayor a 0.'
            });
            return;
        }

        setDespachando(true);
        try {
            // 1) Total ya facturado
            let totalFacturadoAll = 0;
            const esMultiple = ordenesSeleccionadas.length > 1;
            if (esMultiple) {
                for (const o of ordenesSeleccionadas) {
                    const val = parseFloat(o.valorPagar ?? o.valorVenta ?? 0);
                    const { totalFacturado: tf } = await verificarEstadoFacturacion(o.ordenTrabajo, val);
                    totalFacturadoAll += tf;
                }
            } else {
                const { totalFacturado } = await verificarEstadoFacturacion(ordenTrabajo, valorTotalOrden);
                totalFacturadoAll = totalFacturado;
            }

            // 2) Chequeo exceso
            // const nuevoTotal = totalFacturadoAll + montoAFacturar;
            // if (nuevoTotal > valorTotalOrden + 0.01) {
            //     Swal.fire({
            //         icon: 'error',
            //         title: 'Error',
            //         text: `El monto total facturado (${nuevoTotal.toFixed(2)}) excede el valor de la orden (${valorTotalOrden.toFixed(2)})`
            //     });
            //     return;
            // }

            // 3) Preparar datos para la factura
            const fechaActual = new Date().toISOString();

            // Calcular la suma de documentos PUNTOS LACM
            const totalPuntosLacmCobrados = docsAFacturar
                .filter(doc => doc.descripcion?.toLowerCase().includes('puntos'))
                .reduce((sum, doc) => sum + parseFloat(doc.valorPagado || 0), 0);

            console.log('Total PUNTOS LACM cobrados:', totalPuntosLacmCobrados);

            // Determinar el tipo de factura
            const tipoFacturaLocal = dividirFactura 
                ? (isSecondary ? tipoFacturaSec : tipoFacturaPr) 
                : tipoFactura;

            let datosCredito = {
                registroDeExonerados: '',
                ordenDeCompraExenta: '',
                registroSag: '',
                diasCredito: 0,
                ordenDeCompra: ''
            };

            // Si es factura al crédito, solicitar datos adicionales
            if (tipoFacturaLocal === 'credito') {
                // Para facturas de crédito, usar los valores de los campos dinámicos
                const ordenCompraValue = isSecondary 
                    ? (ordenCompraSec || '') 
                    : dividirFactura 
                        ? (ordenCompraPr || '') 
                        : (ordenCompra || '');
                
                const ordenCompraExcentaValue = isSecondary 
                    ? (ordenCompraExcentaSec || '') 
                    : dividirFactura 
                        ? (ordenCompraExcentaPr || '') 
                        : (ordenCompraExcenta || '');
                
                const constanciaRegistroValue = isSecondary 
                    ? (constanciaRegistroSec || '') 
                    : dividirFactura 
                        ? (constanciaRegistroPr || '') 
                        : (constanciaRegistro || '');
                
                const registroSAGValue = isSecondary 
                    ? (registroSAGSec || '') 
                    : dividirFactura 
                        ? (registroSAGPr || '') 
                        : (registroSAG || '');

                const diasCreditoValue = isSecondary 
                    ? diasCreditoSec 
                    : dividirFactura 
                        ? diasCreditoPr 
                        : diasCredito;

                datosCredito = {
                    registroDeExonerados: constanciaRegistroValue,
                    ordenDeCompraExenta: ordenCompraExcentaValue,
                    registroSag: registroSAGValue,
                    diasCredito: diasCreditoValue,
                    ordenDeCompra: ordenCompraValue
                };
            } else {
                // Para facturas de contado, usar los valores existentes
                datosCredito = {
                    registroDeExonerados: constanciaRegistro || '',
                    ordenDeCompraExenta: ordenCompraExcenta || '',
                    registroSag: registroSAG || '',
                    diasCredito: 0,
                    ordenDeCompra: ordenCompra || ''
                };
            }

            const ordersToUpdate =
                Array.isArray(ordenesSeleccionadas) && ordenesSeleccionadas.length > 0
                    ? ordenesSeleccionadas
                    : [{ ordenTrabajo }];

            // Calcular totales solo de los detalles pendientes de facturar
            const detallesPendientesPorOrden = {};
            const todosLosDetallesPorOrden = {};
            let valorVentaFactura = 0;
            let valorDescuentoFactura = 0;

            for (const ord of ordersToUpdate) {
                const idOrden = ord.ordenTrabajo ?? ord.idOrdenMedica;
                const respDetalles = await fetch(
                    `${server}/MgOrdenesMedicasDetalles/idOrdenTrabajo/${idOrden}`
                );
                if (!respDetalles.ok) {
                    throw new Error(`Error al obtener detalles de la orden ${idOrden}`);
                }

                const detallesData = await respDetalles.json();
                const detalles = detallesData.$values || detallesData;
                todosLosDetallesPorOrden[idOrden] = detalles;

                const pendientes = detalles.filter(detalle => !detalle.idFactura || detalle.idFactura === 0);
                detallesPendientesPorOrden[idOrden] = pendientes;

                valorVentaFactura += pendientes.reduce(
                    (sum, det) => sum + parseFloat(det.valorVenta || 0),
                    0
                );
                valorDescuentoFactura += pendientes.reduce(
                    (sum, det) => sum + parseFloat(det.valorDescuento || 0),
                    0
                );
            }

            const idOrdenMedicaParaFactura = ordersToUpdate.length > 0
                ? (ordersToUpdate[0].ordenTrabajo ?? ordersToUpdate[0].idOrdenMedica)
                : (ordenTrabajo || 0);

            console.log('💳 Totales pendientes a facturar (solo sin idFactura):', {
                valorVentaFactura,
                valorDescuentoFactura,
                detallesPendientes: Object.values(detallesPendientesPorOrden).reduce((acc, arr) => acc + arr.length, 0)
            });

            
              // 2) Chequeo exceso
            const totalPermitido = parseFloat(valorVentaFactura || valorTotalOrden || 0);

            if (montoAFacturar > totalPermitido + 0.01) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: `El monto a facturar (${montoAFacturar.toFixed(2)}) excede el valor pendiente (${totalPermitido.toFixed(2)})`
                });
                return;
            }


            const facturaData = {
                idOrdenMedica: idOrdenMedicaParaFactura, // ✅ Garantizado que se envía
                idFormaPago: tipoFacturaLocal === 'credito' ? 2 : 1, // 2 para crédito, 1 para contado
                fechaIngreso: fechaActual,
                ...datosCredito,
                puntosLacm: puntosAFacturar,
                lempirasLacm: totalPuntosLacmCobrados, // Suma de documentos PUNTOS LACM cobrados
                valorVenta: valorVentaFactura,
                porcentajeImpuesto: 0,
                valorImpuesto: 0,
                valorDescuento: valorDescuentoFactura,
                idOrigen: idOrigenValor,
                ingresadoPor: user?.codigoUsuario ?? 'ADMIN'
            };
            console.log('Datos enviados en facturaData:', facturaData);
            console.log('montoAFacturar (neto confirmado):', montoAFacturar);
            console.log('valorVentaFactura (bruto pendientes):', valorVentaFactura);
            console.log('valorDescuentoFactura (pendientes):', valorDescuentoFactura);

            // 5) POST factura TRANSACCIONAL - Todo en una sola llamada al backend
            const detallesCaja = docsAFacturar.map(doc => ({
                idTipoTransaccion: doc.tipoDocumento,
                valor: doc.valorPagado,
                numeroDocumento: '',  // Se asigna en el backend con el número de factura generado
                idEncabezado: cajaAbiertaDatos
            }));

            const porcentajeFacturadoLocal = isSecondary
                ? porcentajeCopago
                : porcentajeAseguradora;

            const despacharPayload = {
                factura: facturaData,
                ordenesAsociadas: ordersToUpdate.map(o => o.ordenTrabajo ?? o.idOrdenMedica),
                idEncabezado: cajaAbiertaDatos ?? null,
                detallesCaja: detallesCaja,
                detallesPoliza: (showPolizaOptions && poliza) ? { porcentajeFacturado: porcentajeFacturadoLocal } : null
            };

            const respFact = await fetch(`${server}/FtFacturas/DespacharFactura`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(despacharPayload)
            });
            if (!respFact.ok) {
                const errorData = await respFact.json();
                throw new Error(errorData.message || 'Error al despachar la factura.');
            }
            
            const { idFactura, numeroFactura } = await respFact.json();

            // 6) Actualizar puntos disponibles después de guardar la factura
            await fetchPuntosLempiras(idCliente);
            
            // 7) finalmente preview
            setFacturaRecienCreada(idFactura);
            await handleFacturaPreview(idFactura);

        } catch (err) {
            console.error(err);
            Swal.fire('Error', 'Ocurrió un problema despachando la factura.', 'error');
        } finally {
            setDespachando(false);
        }
    };

    const handleDespachar1 = async () => {
        if (despachando) return; // Evita múltiples ejecuciones

        const idOrigenValor = clienteOrigenChecked && origen ? origen.idOrigen : null;

        let sucursalActiva = idSucursal;
        if (!sucursalActiva) {
            if (ordenesSeleccionadas.length > 0 && ordenesSeleccionadas[0].idSucursal) {
                sucursalActiva = ordenesSeleccionadas[0].idSucursal;
                setIdSucursal(sucursalActiva);
            }
        }
        if (!sucursalActiva) {
            Swal.fire('Error', 'No se encontró la sucursal para despachar la factura.');
            return;
        }


        // Validar que la caja esté abierta y tenga conteo antes de continuar
        const operacionValida = await validarOperacionCritica('facturación');
        if (!operacionValida) {
            return;
        }

        setDespachando(true);
        try {


            if (cambio < 0) {
                alert('No ha completado el pago de la factura.');
                setDespachando(false);
                return;
            }

            if (!montoConfirmado) {
                alert('Tienen que confirmar el monto a pagar.');
                setDespachando(false);
                return;
            }



            // 1) Si hay varias órdenes, suma el totalFacturado de cada una
            let totalFacturadoAll = 0;
            const esMultiple = Array.isArray(ordenesSeleccionadas) && ordenesSeleccionadas.length > 1;
            if (esMultiple) {
                // recorremos cada orden y extraemos totalFacturado
                for (const o of ordenesSeleccionadas) {
                    const valorTotalDeLaOrden = parseFloat(o.valorPagar ?? o.valorVenta ?? 0);
                    const { totalFacturado: tf } = await verificarEstadoFacturacion(o.ordenTrabajo, valorTotalDeLaOrden);
                    totalFacturadoAll += tf;
                }
            } else {
                // caso única orden
                const { totalFacturado } = await verificarEstadoFacturacion(
                    ordenTrabajo,
                    valorTotalOrden || originalValorFactura
                );
                totalFacturadoAll = totalFacturado;
            }

            // 2) El valor que vamos a facturar ahora es siempre la suma que haya en valorPagar
            const valorAFacturar = dividirFactura
                ? parseFloat(montoClientePrincipal) + parseFloat(montoClienteSecundario)
                : parseFloat(valorPagar);

            // 3) Nuevo total global
            const nuevoTotal = totalFacturadoAll + valorAFacturar;

            const limiteTotal = parseFloat(
                valorTotalOrden ||
                originalValorFactura ||
                0
            );

            console.log('FACTURA>validacion totales', {
                totalFacturadoAll,
                valorAFacturar,
                nuevoTotal,
                limiteTotal,
            });

            if (nuevoTotal > limiteTotal + 0.01) { // Tolerancia de 0.01 para decimales
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: `El monto total facturado (${nuevoTotal.toFixed(2)}) excedería el valor total de la orden (${limiteTotal.toFixed(2)})`,
                });
                setDespachando(false);
                return;
            }
            console.log('Aqui paso 2');
            // Obtener los rangos de facturas para la sucursal
            const responseRangos = await fetch(`${server}/FtRangosDeFacturas/BySucursalUtiliza/${sucursalActiva}`);
            const dataRangos = await responseRangos.json();

            console.log(dataRangos.$values);
            // Obtener la fecha actual
            const fechaActual = new Date();

            // Buscar el rango de factura válido
            const rangoValido = dataRangos.$values.find(rango => {
                const fechaLimite = new Date(rango.fechaLimite);
                const numeroActual = parseInt(rango.numeroActual);
                const numeroMayor = parseInt(rango.numeroMayor);

                return fechaActual <= fechaLimite && numeroActual <= numeroMayor;
            });
            console.log('Aqui paso 3');
            console.log(rangoValido);

            if (!rangoValido) return Swal.fire('Error', 'No se encontró un rango de factura válido.', 'error');

            // Validar numeroActual contra numeroMayor
            const numeroActual = parseInt(rangoValido.numeroActual);
            const numeroMayor = parseInt(rangoValido.numeroMayor);

            if (numeroActual > numeroMayor) {
                alert('Número de factura excede el número límite.');
                return;
            }

            if (numeroActual + (dividirFactura ? 1 : 0) > numeroMayor)
                return Swal.fire('Error', 'No hay suficientes números de factura disponibles.', 'error');

            if (numeroActual + (dividirFactura ? 2 : 0) > numeroMayor)
                return Swal.fire('Error', 'No hay suficientes números de factura disponibles.', 'error');
            // Incrementar numeroActual para usarlo en la factura
            const nuevoNumeroActual = numeroActual + 1;

            // Crear el número de factura en el formato 000-001-01-XXXXXXXX
            const numeroFactura = `${rangoValido.prefijo || '000-001-01'}-${String(nuevoNumeroActual).padStart(8, '0')}`;
            console.log('Aqui paso 4');
            console.log("valorDescuento:", datosFactura?.valorDescuento);
            // Preparar los datos para la API de inserción
            const facturaData = {
                idOrdenMedica: ordenTrabajo ? ordenTrabajo : 0,
                idFormaPago: 1,
                fechaIngreso: fechaActual.toISOString(),
                idRango: rangoValido.idRango,
                numeroFactura: numeroFactura,
                registroDeExonerados: constanciaRegistro,
                ordenDeCompraExenta: ordenCompraExcenta,
                registroSag: registroSAG,
                diasCredito: 0, // Si aplica
                ordenDeCompra: ordenCompra,
                puntosLacm: puntosActuales,
                lempirasLacm: lempirasActuales,
                valorRecargo: 0, // Si aplica
                porcentajeImpuesto: 0, // Si aplica
                valorImpuesto: 0, // Si aplica
                // idDeposito: 0, // Si aplica
                valorVenta: dividirFactura ? montoClientePrincipal : valorPagar,
                valorDescuento: 0, // Si aplica
                // valorDescuento: datosFactura?.valorDescuento || 0,
                idOrigen: null,
                ingresadoPor: user ? user.codigoUsuario : 'ADMIN',
                // Agrega otros campos si es necesario
            };

            console.log(facturaData);

            // Realizar el POST a la API para insertar la factura
            const responseFactura = await fetch(`${server}/FtFacturas`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(facturaData),
            });
            console.log(responseFactura);
            if (!responseFactura.ok) {
                throw new Error('Error al despachar la factura.');
            }
            console.log('Aqui paso 5');
            let facturaData2;
            if (dividirFactura) {
                const numeroFactura2 = `${rangoValido.prefijo || '000-001-01'}-${String(nuevoNumeroActual + 1).padStart(8, '0')}`;

                facturaData2 = {
                    idOrdenMedica: ordenTrabajo,
                    idFormaPago: 1,
                    fechaIngreso: fechaActual.toISOString(),
                    idRango: rangoValido.idRango,
                    numeroFactura: numeroFactura2,
                    registroDeExonerados: constanciaRegistro,
                    ordenDeCompraExenta: ordenCompraExcenta,
                    registroSag: registroSAG,
                    diasCredito: 0, // Si aplica
                    ordenDeCompra: ordenCompra,
                    puntosLacm: 0,
                    lempirasLacm: 0,
                    valorRecargo: 0, // Si aplica
                    porcentajeImpuesto: 0, // Si aplica
                    valorImpuesto: 0, // Si aplica
                    // idDeposito: 0, // Si aplica
                    valorVenta: dividirFactura ? montoClienteSecundario : valorPagar,
                    valorDescuento: 0, // Si aplica
                    idOrigen: idOrigenValor,
                    ingresadoPor: user ? user.codigoUsuario : 'ADMIN',
                };

                const responseFactura2 = await fetch(`${server}/FtFacturas`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(facturaData2),
                });

                if (!responseFactura2.ok) {
                    throw new Error('Error al despachar la factura.');
                }
            }

            const resultFactura = await responseFactura.json();
            const idFactura = resultFactura.idFactura;

            if (!idFactura) {
                throw new Error('No se recibió el id de la factura');
            }
            console.log('Aqui paso 6');

            // 2) Ahora guardamos la relación factura ⇆ órdenes de trabajo
            //    recorremos el array ordenesSeleccionadas
            for (const orden of ordenesSeleccionadas) {
                await fetch(`${server}/FtFacturasOrdenes`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        idFactura,
                        idOrdenMedica: orden.ordenTrabajo,
                        ingresadoPor: user?.codigoUsuario || 'ADMIN'
                    })
                });
            }

            const dataPuntosCliente = {
                idCliente: datosFactura.idCliente ? datosFactura.idCliente : idCliente,
                idTipo: 1,
                idTipoAsignacion: 1,
                puntos: valorPagar,
                documento: numeroFactura,
                ingresadoPor: user ? user.codigoUsuario : 'ADMIN'
            }
            console.log(dataPuntosCliente);
            const responseDetallePuntosC = await fetch(`${server}/MgClientesPuntoes`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(dataPuntosCliente),
            });

            if (!responseDetallePuntosC.ok) {
                throw new Error('Error al guardar el detalle de puntos de la factura');
            }
            const resultPuntosC = await responseDetallePuntosC.json();
            console.log(resultPuntosC.id);
            const idPuntoC = resultPuntosC?.id;
            if (!idPuntoC) {
                throw new Error('No se recibió el id en el resultado de resultPuntosC');
            }
            console.log('idPuntosC:', idPuntoC);

            if (dividirFactura && !clienteOrigenChecked) {
                try {
                    const resultFactura = await responseFactura2.json();
                    const idFactura = resultFactura.idFactura;

                    if (!idFactura) {
                        throw new Error('No se recibió el id de la factura');
                    }
                    const dataPuntosCliente = {
                        idCliente: datosFactura.idCliente ? datosFactura.idCliente : idCliente,
                        idTipo: 1,
                        idTipoAsignacion: 1,
                        puntos: montoClienteSecundario,
                        documento: numeroFactura,
                        ingresadoPor: user ? user.codigoUsuario : 'ADMIN'
                    }
                    console.log(dataPuntosCliente);
                    const responseDetallePuntosC = await fetch(`${server}/MgClientesPuntoes`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(dataPuntosCliente),
                    });

                    if (!responseDetallePuntosC.ok) {
                        throw new Error('Error al guardar el detalle de puntos de la factura');
                    }
                    const resultPuntosC = await responseDetallePuntosC.json();
                    console.log(resultPuntosC.id);
                    const idPuntoC = resultPuntosC?.id;
                    if (!idPuntoC) {
                        throw new Error('No se recibió el id en el resultado de resultPuntosC');
                    }
                    console.log('idPuntosC:', idPuntoC);
                } catch (error) {

                }

            }

            // Ahora, por cada documento, hacemos la llamada a la API
            for (const documento of documentos) {


                const payload = {
                    idTipoTransaccion: documento.tipoDocumento,
                    valor: parseFloat(documento.valorPagado),
                    numeroDocumento: numeroFactura,
                    ingresadoPor: user?.codigoUsuario || 'ADMIN',
                    idEncabezado: cajaAbiertaDatos,
                    idFactura: idFactura
                };

                // Guardar el movimiento de caja
                try {
                    const response = await fetch(`${server}/MgCajaArqueoDetalleSistemas`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                    });
                    
                    if (response.ok) {
                        console.log('Movimiento de caja guardado exitosamente');
                    } else {
                        console.error('Error al guardar el movimiento de caja:', response.statusText);
                    }
                } catch (error) {
                    console.error('Error al guardar el movimiento de caja:', error);
                }

                if (
                    documento.descripcion &&
                    documento.descripcion.toLowerCase().includes('puntos')
                ) {
                    const dataPuntos = {
                        fecha: fechaActual.toISOString().split('T')[0],
                        idTipo: 1,
                        idComercio: 1,
                        documento: numeroFactura,
                        ingresadoPor: user ? user.codigoUsuario : 'ADMIN',
                    }
                    console.log('Puntos Cobro a guardar:', dataPuntos);
                    const responsePuntos = await fetch(`${server}/MgClientesPuntosCobroes`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(dataPuntos),
                    });

                    if (!responsePuntos.ok) {

                        throw new Error('Error al guardar el detalle de pago de la factura');
                    } else {

                        const resultPuntos = await responsePuntos.json();
                        const id = resultPuntos.id;
                        if (!id) {
                            throw new Error('No se recibió el id del punto de cobro');
                        } else {
                            const dataCobro = {
                                idCobro: id,
                                idPunto: idPuntoC,
                                puntos: (documento.valorPagado / 0.30),
                                ingresadoPor: user ? user.codigoUsuario : 'ADMIN',
                            }
                            console.log('Detalle Cobro a guardar:', dataCobro);
                            const responseDetallePuntos = await fetch(`${server}/MgClientesPuntosCobrosDetalles`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                },
                                body: JSON.stringify(dataCobro),
                            });
                            if (!responseDetallePuntos.ok) {
                                throw new Error('Error al guardar el detalle de pago de la factura');
                            }
                        }
                    }
                }

            }
            // Actualizar el numeroActual en el rango de facturas
            const rangoActualizado = {
                ...rangoValido,
                numeroActual: dividirFactura ? nuevoNumeroActual + 1 : nuevoNumeroActual,
            };
            console.log(rangoActualizado);
            const responseActualizarRango = await fetch(`${server}/FtRangosDeFacturas/${rangoValido.idRango}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(rangoActualizado),
            });

            if (!responseActualizarRango.ok) {
                throw new Error('Error al actualizar el número de factura en el rango.');
            }


            // Preparamos la lista de órdenes a procesar:
            // - Si hay varias, usamos ordenesSeleccionadas
            // - Si es solo una, usamos el campo ordenTrabajo
            const ordersToUpdate =
                Array.isArray(ordenesSeleccionadas) && ordenesSeleccionadas.length > 0
                    ? ordenesSeleccionadas
                    : [{ ordenTrabajo }];

            // Campos que queremos conservar al actualizar
            const camposDeseados = [
                "idDetalle", "descripcionTipoAnalisis", "valorVenta", "comentarios",
                "idEquipo", "ingresadoPor", "idMetodo", "idModulo",
                "idOrdenMedica", "idTipoAnalisis", "idTipoPrecio",
                "valorDescuento", "evaluada"
            ];

            for (const ord of ordersToUpdate) {
                // extraemos el identificador real de la orden
                const idOrden = ord.ordenTrabajo ?? ord.idOrdenMedica;

                // 1) Traemos sus detalles
                const respDetalles = await fetch(
                    `${server}/MgOrdenesMedicasDetalles/idOrdenTrabajo/${idOrden}`
                );
                if (!respDetalles.ok) {
                    throw new Error(`Error al obtener detalles de la orden ${idOrden}`);
                }
                const detallesData = await respDetalles.json();
                const detalles = detallesData.$values || detallesData;

                // Filtrar solo detalles sin idFactura o con idFactura = 0
                const detallesAActualizar = detalles.filter(detalle => 
                    !detalle.idFactura || detalle.idFactura === 0
                );

                // 2) Por cada detalle, hacemos el PUT con el idFactura
                for (const detalle of detallesAActualizar) {
                    const detalleActualizado = {
                        ...pickDetailFields(detalle, camposDeseados),
                        modificadoPor: user?.codigoUsuario ?? 'ADMIN',
                        idFactura
                    };
                    const respUpd = await fetch(
                        `${server}/MgOrdenesMedicasDetalles/${detalle.idDetalle}`,
                        {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(detalleActualizado),
                        }
                    );
                    if (!respUpd.ok) {
                        throw new Error(
                            `Error al actualizar el detalle ${detalle.idDetalle} de la orden ${idOrden}`
                        );
                    }
                }
            }


            // Mostrar vista previa de la factura recién creada
            setFacturaRecienCreada(idFactura);
            await handleFacturaPreview(idFactura);

        } catch (error) {
            console.log('Error al despachar la factura:', error);
            alert('Error al despachar la factura. Por favor, intenta nuevamente.');
        } finally {
            setDespachando(false);
        }
    };

    function pickDetailFields(detail, fields) {
        return fields.reduce((acc, field) => {
            if (detail[field] !== undefined) {
                acc[field] = detail[field];
            }
            return acc;
        }, {});
    }

    const handleCancelar = () => {
        // Resetear todos los campos
        setOrdenTrabajo('');
        setNombreCliente('');
        setIdentidad('');
        setPuntosActuales('');
        setLempirasActuales('');
        setValorPagar('');
        setMontoConfirmado(false);
        setOrdenCompra('');
        setOrdenCompraExcenta('');
        setConstanciaRegistro('');
        setRegistroSAG('');
        setTipoDocumento('');
        setNumero('');
        setValorPagado('');
        setDocumentos([]);
        setCambio(0);
        setTipoComprobante('Ticket');
        onClose();
    };
    const handleWhatsapp = async (idFactura) => {
        onClose();
        const { value: numeroWhatsapp } = await Swal.fire({
            title: 'Ingrese el número de WhatsApp',
            input: 'text',
            inputLabel: 'Número de WhatsApp',
            inputPlaceholder: '+50433333333',
            showCancelButton: true,
            inputValidator: (value) => {
                if (!value) {
                    return 'Por favor ingrese un número de WhatsApp';
                }
                // Validar que el número de WhatsApp tenga un formato adecuado
                const whatsappRegex = /^\+?[0-9]{1,4}[0-9]{10,15}$/;
                if (!whatsappRegex.test(value)) {
                    return 'Por favor ingrese un número de WhatsApp válido, incluyendo el código de área si es necesario';
                }
            }
        });

        if (numeroWhatsapp) {
            try {
                const response = await fetch(`${server}/Whatsapp/PostFacturaWhatsapp?idFactura=${idFactura}&numeroWhatsapp=${numeroWhatsapp}`, {
                    method: 'POST',
                });

                if (response.ok) {
                    Swal.fire({
                        icon: 'success',
                        title: 'WhatsApp Enviado',
                        showConfirmButton: false,
                        timer: 1500
                    });
                } else {
                    const errorData = await response.json();
                    const errorMessage = errorData.message || (errorData.errors ? Object.values(errorData.errors).flat().join(', ') : 'Ocurrió un error inesperado');
                    Swal.fire({
                        icon: 'error',
                        title: `Error ${response.status}`,
                        text: errorMessage,
                    });
                }
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error al enviar el WhatsApp',
                    text: error || 'Ocurrió un error inesperado',
                });
            }
        }
    };
    
    // Método para enviar correo electrónico con la factura
    const handleEnviarCorreo = async (idFactura) => {
        try {
            // Primero cerramos el modal actual para evitar problemas de foco
            onClose();
            
            // Pequeño retraso para asegurar que el modal principal se cierre completamente
            await new Promise(resolve => setTimeout(resolve, 100));
            
            const { value: email } = await Swal.fire({
                title: 'Enviar factura por correo',
                input: 'email',
                inputLabel: 'Dirección de correo electrónico',
                inputPlaceholder: 'Ingrese el correo electrónico',
                showCancelButton: true,
                allowOutsideClick: false,
                heightAuto: true,
                focusConfirm: false,
                inputAutoFocus: true,
                inputValidator: (value) => {
                    if (!value) {
                        return 'Por favor ingrese una dirección de correo válida';
                    }
                }
            });

            if (email) {
                const response = await fetch(`${server}/Correo/enviar-factura?idFactura=${idFactura}&destinatario=${encodeURIComponent(email)}&asunto=Factura%20Laboratorio`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });
                console.log('Respuesta del envío de correo:', response);
                if (response.ok) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Correo enviado',
                        text: 'La factura ha sido enviada por correo electrónico',
                        showConfirmButton: false,
                        timer: 1500
                    });
                } else {
                    const errorData = await response.json();
                    Swal.fire({
                        icon: 'error',
                        title: 'Error al enviar el correo',
                        text: errorData.message || 'Ocurrió un error inesperado'
                    });
                }
            }
        } catch (error) {
            console.error("Error al enviar correo:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo enviar el correo'
            });
        }
    };

    useEffect(() => {
        if (!datosFactura) return;
        console.log('Si llego aqui', datosFactura);
        fetchPuntosLempiras(datosFactura.idCliente);
    }, [datosFactura]);

    useEffect(() => {
        if (!datosFactura || datosFactura.multiple) return;
        console.log(datosFactura);
        const idOrd = datosFactura.ordenTrabajo;
        setOrdenTrabajo(datosFactura.ordenTrabajo || '');
        setNombreCliente(datosFactura.nombreCliente || '');
        setIdentidad(datosFactura.identidad || '');

        // Guardamos el valor total de la orden (preferimos valorVenta para mantener pendiente correcto)
        const valorTotal = datosFactura.valorVenta != null
            ? parseFloat(datosFactura.valorVenta)
            : datosFactura.TotalPagar
                ? parseFloat(datosFactura.TotalPagar)
                : parseFloat(datosFactura.valorPagar) || 0;

        setValorTotalOrden(valorTotal);

        // Verificamos estado de facturación y calculamos monto pendiente
        const verificarFacturacion = async () => {
            if (datosFactura.ordenTrabajo) {
                const { montoPendiente } = await verificarEstadoFacturacion(
                    datosFactura.ordenTrabajo,
                    valorTotal
                );

                // Sugerimos el monto pendiente como valor a pagar
                setValorPagar(parseFloat(montoPendiente).toFixed(2));
            }
        };

        verificarFacturacion();
        setIdSucursal(datosFactura.idSucursal || '');
        setIdFormaPago(datosFactura.idFormaPago || '');
        // fetchPuntosLempiras(datosFactura.idCliente);


        (async () => {
            try {
                const resp = await fetch(
                    `${server}/MgOrdenesMedicasDetallePolizas/porOrden/${idOrd}`
                );
                if (!resp.ok) throw new Error('No hay póliza');
                const raw = await resp.json();
                console.log('poliza', raw);
                const item = raw.$values?.[0];          // <-- aquí
                if (item?.idOrdenMedica) {
                    const copago = parseFloat(item.porcentajeCopago)  // ajusta el campo a tu API
                    setPorcentajeCopago(copago)
                    setPorcentajeAseguradora(100 - copago)
                    console.log('Poliza encontrada');
                    setPoliza(item);
                    setShowPolizaOptions(true);

                    if (!dividirFactura) {
                        if (polizaOption === 'aseguradora')
                            setValorPagar(item.aseguradora.toFixed(2));
                        else if (polizaOption === 'asegurado')
                            setValorPagar(item.asegurado.toFixed(2));
                        else
                            setValorPagar(valorTotalOrden.toFixed(2));
                    } else {
                        setPolizaSplitOption('noElegibleCopago');
                    }
                }
            } catch {
                setShowPolizaOptions(false);
            }
        })();


    }, [dividirFactura, polizaOption]);

    useEffect(() => {
        if (!dividirFactura || !poliza) return;

        let principal;
        let secundario;

        if (polizaSplitOption === 'noElegibleCopago') {
            // Principal = aseguradora + copago
            principal = poliza.aseguradora + poliza.copago;
            // Secundario = noElegible
            secundario = poliza.noElegible;
        } else {
            // Principal = aseguradora
            principal = poliza.aseguradora;
            // Secundario = copago + noElegible
            secundario = poliza.copago + poliza.noElegible;
        }

        // Pasa a string con 2 decimales
        const pStr = principal.toFixed(2);
        const sStr = secundario.toFixed(2);

        setMontoClientePrincipal(pStr);
        setMontoClienteSecundario(sStr);

        // Actualiza también el valor total a pagar
        setValorPagar((principal + secundario).toFixed(2));
    }, [dividirFactura, poliza, polizaSplitOption]);


    const handleCerrarFactura = () => {
        // Cerrar cualquier ventana emergente que pueda estar abierta
        setOpenFacturaPreview(false);
        setFacturaPdfUrl(null);
        setFacturaPreviewError(null);
        setFacturaRecienCreada(null);

        // Cerrar la ventana principal
        if (onClose) {
            onClose();
        }
    };

    // Funciones para vista previa de PDF de factura
    const handleCloseFacturaPreview = () => {
        setOpenFacturaPreview(false);
        setFacturaPdfUrl(null);
        setFacturaPreviewError(null);
        setFacturaRecienCreada(null);
        setFacturaPreviewFormato('Carta');
        onClose();
    };

    const handleFacturaPreview = async (idFactura, formato = null) => {
        setFacturaPreviewLoading(true);
        setFacturaPreviewError(null);
        setOpenFacturaPreview(true);

        try {
            // Usar el formato especificado o el seleccionado en el radio button
            const formatoSeleccionado = formato || tipoComprobante;
            setFacturaPreviewFormato(formatoSeleccionado);

            const endpoint = formatoSeleccionado === 'Carta'
                ? `/FtFacturas/ImprimirFactura/${idFactura}`
                : `/FtFacturas/ImprimirFacturaticket/${idFactura}`;

            const response = await fetch(`${server}${endpoint}`, {
                method: 'POST',
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                setFacturaPdfUrl(url);
            } else {
                const errorData = await response.json();
                const errorMessage = errorData.message || 'Error al generar la vista previa del PDF';
                setFacturaPreviewError(errorMessage);
            }
        } catch (error) {
            console.error('Error en vista previa de factura:', error);
            setFacturaPreviewError('Error al generar la vista previa del PDF');
        } finally {
            setFacturaPreviewLoading(false);
        }
    };

    const handleConfirmFacturaPreview = () => {
        handleCloseFacturaPreview();
        onClose();
        onClose();
        Swal.fire({
            icon: 'success',
            title: 'Factura despachada exitosamente',
            showConfirmButton: false,
            timer: 1500,
        });
    };

    // Centraliza el cierre de todos los popups secundarios
    const closeAllPopups = () => {
        setOpenFacturaPreview(false);
        // Agrega aquí otros setOpenX(false) de modales secundarios si existen
    };

    // Escape cierra popups secundarios y notifica al padre para cerrar todo
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                closeAllPopups();
                if (onClose) onClose('closeAll');
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);
    const handleConfirmarYDespachar = () => {
        // Cierra todos los popups secundarios (vista previa, factura, etc.)
        closeAllPopups();

        // Notifica al componente padre que debe cerrar todos sus modales también
        if (onClose) onClose('closeAll');
       

        // Navega directamente a la lista de órdenes de trabajo
        navigate('/OrdenesTrabajo');

        // Envía un evento global para solicitar el cierre del modal de orden de trabajo
        // que puede estar abierto desde Clientes.jsx
        try {
            if (window.dispatchEvent) {
                window.dispatchEvent(new CustomEvent('closeOrdenTrabajoModal'));
            }
        } catch (e) {
            console.error("Error al despachar evento de cierre:", e);
        }
    };

    // Handler para Cancelar (cierra popups secundarios y notifica al padre)
    const handleCancelarPopup = () => {
        closeAllPopups();
        if (onClose) onClose('closeAll');

        // Ir a la lista de órdenes médicas
    navigate('/OrdenesTrabajo');
    };


    useEffect(() => {
        if (!datosFactura) return;
        const ordenes = datosFactura.ordenes;
        const esMultiple = Array.isArray(ordenes) && ordenes.length > 1;

        if (esMultiple) {
            // 1) suma original de todas las órdenes
            const sumaOriginal = ordenes.reduce((acc, o) => {
                const v = parseFloat(o.valorPagar ?? o.valorVenta ?? 0);
                return acc + (isNaN(v) ? 0 : v);
            }, 0);

            // 2) pide cada pendiente y 3) suma los resultados
            const promesas = ordenes.map(o =>
                verificarEstadoFacturacion(
                    o.ordenTrabajo,
                    parseFloat(o.valorPagar ?? o.valorVenta ?? 0)
                ).then(res => res.montoPendiente)
            );

            Promise.all(promesas).then(pendientes => {
                const sumaPend = pendientes.reduce((a, p) => a + p, 0);

                // 4) estado global
                let estado;
                if (Math.abs(sumaPend) < 0.01) estado = 'facturado-completo';
                else if (sumaPend < sumaOriginal) estado = 'facturado-parcial';
                else estado = 'sin-facturar';

                // 5) actualiza TODOS los estados
                setTotalOrdenes(sumaOriginal);
                setValorTotalOrden(sumaOriginal);
                setPendienteTotal(sumaPend);
                setEstadoFacturacion(estado);

                // ¡Y aquí cargas la suma de pendientes en valorPagar!
                setValorPagar(sumaPend.toFixed(2));
                // también actualizas montoPendiente si lo usas en otro lado
                setMontoPendiente(sumaPend);
            });
        }
        else {
            // Caso single
            const valorTotal = parseFloat(
                datosFactura.valorVenta ??
                datosFactura.TotalPagar ??
                datosFactura.valorPagar ??
                0
            );
            setTotalOrdenes(valorTotal);
            setValorTotalOrden(valorTotal);
            // montoPendiente ya lo calculaste en tu otro useEffect individual
            setPendienteTotal(montoPendiente);
            setEstadoFacturacion(
                montoPendiente <= 0
                    ? 'facturado-completo'
                    : montoPendiente < valorTotal
                        ? 'facturado-parcial'
                        : 'sin-facturar'
            );
            setValorPagar(montoPendiente.toFixed(2));
        }
    }, [datosFactura, montoPendiente]);


    const handleFacturaCredito = async () => {
        // aquí va tu lógica para facturar a crédito...
        try {
            // validaciones, fetch, etc.
            Swal.fire('Crédito', 'Se ha procesado la factura a crédito.', 'success');
        } catch (err) {
            console.error(err);
            Swal.fire('Error', 'No se pudo procesar la factura a crédito.', 'error');
        }
    };
    
    // Método para abrir el modal de nuevo recibo con datos iniciales
    const handleNuevoRecibo = async (factura) => {
        // Verificar que sea una factura a crédito
        if (factura.idFormaPago !== 2) {
            Swal.fire({
                icon: 'warning',
                title: 'Operación no permitida',
                text: 'Solo se pueden crear recibos para facturas a crédito'
            });
            return;
        }
        
        try {
            // PASO 1: Obtener recibos actualizados directamente de la API
            console.log('🔄 Obteniendo recibos actualizados para factura:', factura.idFactura);
            const response = await fetch(`${server}/MgRecibos/PorFactura/${factura.idFactura}`);
            
            let recibosDeFactura = [];
            
            if (response.ok) {
                const data = await response.json();
                console.log('📦 Datos de API recibidos:', data);
                
                // Deserializar la estructura con $values
                let recibosArray = [];
                if (data && data.$values && Array.isArray(data.$values)) {
                    recibosArray = data.$values;
                } else if (Array.isArray(data)) {
                    recibosArray = data;
                }
                
                // Procesar cada recibo
                recibosDeFactura = recibosArray.map(recibo => ({
                    ...recibo,
                    mgRecibosDetalles: recibo.mgRecibosDetalles?.$values || recibo.mgRecibosDetalles || [],
                    MgRecibosDetalles: recibo.MgRecibosDetalles?.$values || recibo.MgRecibosDetalles || []
                }));
                
                console.log('✅ Recibos procesados:', recibosDeFactura.length);
            } else {
                console.log('⚠️ No se encontraron recibos (404 o error)');
            }
            
            // PASO 2: Calcular el Saldo Anterior del nuevo recibo
            // Siempre calcular desde el monto real de la factura menos lo abonado,
            // para evitar inconsistencias si los recibos previos tenían saldos incorrectos.
            const montoBruto = roundAmount(factura.valorVenta || 0);
            const descuentoFactura = roundAmount(factura.valorDescuento || 0);
            const montoTotalFactura = roundAmount(montoBruto - descuentoFactura);
            
            const totalAbonado = sumAmounts(
                recibosDeFactura.map(recibo => recibo.ValorRecibo || recibo.valorRecibo || 0)
            );
            
            const saldoAnteriorNuevoRecibo = roundAmount(montoTotalFactura - totalAbonado);
            
            console.log('📊 Recibos encontrados:', recibosDeFactura.length);
            console.log('🧾 Cálculo de saldo:');
            console.log('   - Monto Bruto:', montoBruto);
            console.log('   - Descuento:', descuentoFactura);
            console.log('   - Monto Total Factura:', montoTotalFactura);
            console.log('   - Total Abonado:', totalAbonado);
            console.log('   - Saldo Pendiente (Saldo Anterior nuevo recibo):', saldoAnteriorNuevoRecibo);
            
            // PASO 3: Inicializar datos del nuevo recibo
            console.log('📝 Inicializando nuevo recibo con Saldo Anterior:', saldoAnteriorNuevoRecibo);
            
            setNuevoReciboData({
                idCliente: idCliente,
                valorRecibo: 0,
                saldoAnterior: saldoAnteriorNuevoRecibo,
                saldoActual: saldoAnteriorNuevoRecibo,
                concepto: `Abono a factura ${factura.numeroFactura}`,
                detalles: [{
                    idFactura: factura.idFactura,
                    saldoInicial: saldoAnteriorNuevoRecibo,
                    abono: 0,
                    saldoFinal: saldoAnteriorNuevoRecibo
                }],
                documentosPago: []
            });
            
            setOpenNuevoRecibo(true);
            
        } catch (error) {
            console.error('❌ Error al obtener recibos:', error);
            Swal.fire('Error', 'No se pudieron cargar los recibos previos', 'error');
        }
    };
    
    // Método para abrir el modal de nuevo recibo con múltiples facturas
    const handleNuevoReciboGeneral = () => {
        // Filtrar solo las facturas a crédito con saldo pendiente
        const facturasCredito = facturas.filter(f => f?.idFormaPago === 2);
        
        const detallesIniciales = facturasCredito.map(factura => {
            // Filtrar recibos de esta factura específica
            const recibosDeFactura = recibos.filter(r => {
                const detalles = r.MgRecibosDetalles || r.mgRecibosDetalles;
                return detalles && detalles.some(det => {
                    const facturaId = det.IdFactura || det.idFactura;
                    return facturaId === factura.idFactura;
                });
            });
            
            // Variable para el saldo inicial del detalle
            let saldoInicialDetalle;
            
            if (recibosDeFactura.length > 0) {
                // SI EXISTEN RECIBOS PREVIOS: Buscar el último recibo por ID
                const recibosOrdenados = [...recibosDeFactura].sort((a, b) => {
                    const idA = a.IdRecibo || a.idRecibo || 0;
                    const idB = b.IdRecibo || b.idRecibo || 0;
                    return idB - idA; // Descendente
                });
                
                const ultimoRecibo = recibosOrdenados[0];
                
                // Buscar el detalle de esta factura en el último recibo
                const detalles = ultimoRecibo.MgRecibosDetalles || ultimoRecibo.mgRecibosDetalles;
                const detalleFactura = detalles?.find(det => {
                    const facturaId = det.IdFactura || det.idFactura;
                    return facturaId === factura.idFactura;
                });
                
                // Usar el Saldo Final del último recibo
                saldoInicialDetalle = roundAmount(detalleFactura?.SaldoFinal || detalleFactura?.saldoFinal || 0);
            } else {
                // SI NO HAY RECIBOS PREVIOS: Usar el valor total de la factura
                saldoInicialDetalle = roundAmount(factura.valorVenta || 0);
            }
            
            return {
                idFactura: factura.idFactura,
                numeroFactura: factura.numeroFactura,
                saldoInicial: saldoInicialDetalle,
                abono: 0,
                saldoFinal: saldoInicialDetalle
            };
        }).filter(detalle => detalle.saldoInicial > 0); // Solo incluir facturas con saldo pendiente
        
        const saldoTotalPendiente = sumAmounts(detallesIniciales.map(det => det.saldoInicial));
        
        setNuevoReciboData({
            idCliente: idCliente,
            valorRecibo: 0,
            saldoAnterior: saldoTotalPendiente,
            saldoActual: saldoTotalPendiente,
            concepto: 'Abono general a múltiples facturas',
            detalles: detallesIniciales,
            documentosPago: []
        });
        
        setOpenNuevoRecibo(true);
    };
    
    // Método para agregar un detalle al recibo
    const handleAgregarDetalleRecibo = () => {
        setNuevoReciboData(prev => ({
            ...prev,
            detalles: [
                ...prev.detalles,
                {
                    idFactura: '',
                    saldoInicial: 0,
                    abono: 0,
                    saldoFinal: 0
                }
            ]
        }));
    };
    
    // Método para eliminar un detalle del recibo
    const handleEliminarDetalleRecibo = (index) => {
        const nuevosDetalles = [...nuevoReciboData.detalles];
        nuevosDetalles.splice(index, 1);
        
        // Recalcular valor del recibo
        const nuevoValorRecibo = sumAmounts(nuevosDetalles.map(det => det.abono || 0));
        
        setNuevoReciboData(prev => ({
            ...prev,
            detalles: nuevosDetalles,
            valorRecibo: nuevoValorRecibo,
            saldoActual: roundAmount(prev.saldoAnterior - nuevoValorRecibo)
        }));
    };
    
    // Método para actualizar los detalles del recibo
    const handleUpdateDetalleRecibo = (index, field, value) => {
        const nuevosDetalles = [...nuevoReciboData.detalles];
        const numericFields = ['saldoInicial', 'abono'];
        const normalizedValue = numericFields.includes(field) ? roundAmount(value) : value;
        nuevosDetalles[index] = {
            ...nuevosDetalles[index],
            [field]: normalizedValue
        };
        
        // Recalcular saldoFinal si cambia saldoInicial o abono
        if (field === 'saldoInicial' || field === 'abono') {
            const saldoInicial = roundAmount(nuevosDetalles[index].saldoInicial || 0);
            const abono = roundAmount(nuevosDetalles[index].abono || 0);
            nuevosDetalles[index].saldoFinal = roundAmount(saldoInicial - abono);
        }
        
        // Recalcular valor total del recibo
        const nuevoValorRecibo = sumAmounts(nuevosDetalles.map(det => det.abono || 0));
        
        setNuevoReciboData(prev => ({
            ...prev,
            detalles: nuevosDetalles,
            valorRecibo: nuevoValorRecibo,
            saldoActual: roundAmount(prev.saldoAnterior - nuevoValorRecibo)
        }));
    };
    
    // Métodos para gestionar documentos de pago en el recibo
    const handleAgregarDocumentoPago = () => {
        setNuevoReciboData(prev => ({
            ...prev,
            documentosPago: [
                ...prev.documentosPago,
                {
                    tipoDocumento: '',
                    numero: '',
                    valorPagado: 0,
                    descripcion: ''
                }
            ]
        }));
    };
    
    const handleEliminarDocumentoPago = (index) => {
        const nuevosDocumentos = [...(nuevoReciboData.documentosPago || [])];
        nuevosDocumentos.splice(index, 1);
        
        setNuevoReciboData(prev => ({
            ...prev,
            documentosPago: nuevosDocumentos
        }));
    };
    
    const handleUpdateDocumentoPago = (index, field, value) => {
        const nuevosDocumentos = [...(nuevoReciboData.documentosPago || [])];
        const normalizedValue = field === 'valorPagado' ? roundAmount(value) : value;
        nuevosDocumentos[index] = {
            ...nuevosDocumentos[index],
            [field]: normalizedValue
        };
        
        // Si el tipo de documento cambió, buscar y actualizar la descripción
        if (field === 'tipoDocumento') {
            const descripcion = transaccionesCajero.find(d => d.id === value)?.descripcionTipoTransaccion || '';
            nuevosDocumentos[index].descripcion = descripcion;
        }
        
        setNuevoReciboData(prev => ({
            ...prev,
            documentosPago: nuevosDocumentos
        }));
    };
    
    // Método para cargar un recibo existente con sus detalles y documentos
    const handleEditarRecibo = async (recibo) => {
        try {
            // Cargar detalles del recibo
            const responseDetalles = await fetch(`${server}/MgRecibosDetalles/PorRecibo/${recibo.IdRecibo || recibo.idRecibo}`);
            let detallesRecibo = [];
            
            if (responseDetalles.ok) {
                const dataDetalles = await responseDetalles.json();
                detallesRecibo = (dataDetalles.$values || dataDetalles || []).map(det => ({
                    idFactura: det.IdFactura || det.idFactura,
                    saldoInicial: roundAmount(det.SaldoInicial || det.saldoInicial || 0),
                    abono: roundAmount(det.Abono || det.abono || 0),
                    saldoFinal: roundAmount(det.SaldoFinal || det.saldoFinal || 0)
                }));
            }
            
            // Cargar documentos de pago del recibo
            const responseDocumentos = await fetch(`${server}/MgRecibosDocumentosPago/PorRecibo/${recibo.IdRecibo || recibo.idRecibo}`);
            let documentosPago = [];
            
            if (responseDocumentos.ok) {
                const dataDocumentos = await responseDocumentos.json();
                documentosPago = (dataDocumentos.$values || dataDocumentos || []).map(doc => ({
                    id: doc.Id || doc.id,
                    tipoDocumento: doc.IdTipoDocumento || doc.idTipoDocumento,
                    numero: doc.Numero || doc.numero || '',
                    valorPagado: roundAmount(doc.Valor || doc.valor || 0),
                    descripcion: doc.IdTipoDocumentoNavigation?.Descripcion || doc.idTipoDocumentoNavigation?.descripcion || ''
                }));
            }
            
            // Calcular valor del recibo desde los detalles
            const valorRecibo = sumAmounts(detallesRecibo.map(det => det.abono));
            
            setNuevoReciboData({
                idRecibo: recibo.IdRecibo || recibo.idRecibo,
                idCliente: recibo.IdCliente || recibo.idCliente,
                valorRecibo: valorRecibo,
                saldoAnterior: roundAmount(recibo.SaldoAnterior || recibo.saldoAnterior || 0),
                saldoActual: roundAmount(recibo.SaldoActual || recibo.saldoActual || 0),
                concepto: recibo.Concepto || recibo.concepto || '',
                detalles: detallesRecibo,
                documentosPago: documentosPago,
                modoEdicion: true
            });
            
            setOpenNuevoRecibo(true);
        } catch (error) {
            console.error('Error al cargar recibo para edición:', error);
            Swal.fire('Error', 'No se pudo cargar el recibo para editar', 'error');
        }
    };
    
    // Validar que los documentos de pago no excedan el valor del recibo
    const validarDocumentosPago = () => {
        const totalDocumentosCents = (nuevoReciboData.documentosPago || []).reduce(
            (acc, doc) => acc + toCents(doc.valorPagado || 0),
            0
        );
        const valorReciboCents = toCents(nuevoReciboData.valorRecibo || 0);
        const totalDocumentos = centsToAmount(totalDocumentosCents);
        const valorRecibo = centsToAmount(valorReciboCents);
        
        if (totalDocumentosCents > valorReciboCents) {
            return {
                valido: false,
                mensaje: `La suma de los documentos de pago (L. ${totalDocumentos.toFixed(2)}) excede el valor del recibo (L. ${valorRecibo.toFixed(2)})`
            };
        }
        
        if (totalDocumentosCents < valorReciboCents && totalDocumentosCents > 0) {
            return {
                valido: false,
                mensaje: `La suma de los documentos de pago (L. ${totalDocumentos.toFixed(2)}) es menor al valor del recibo (L. ${valorRecibo.toFixed(2)}). Debe ser exacto.`
            };
        }
        
        return { valido: true, mensaje: '' };
    };
    
    // ============================================================
    // FUNCIONES PARA NOTAS DE CRÉDITO (DEVOLUCIONES)
    // ============================================================
    
    // Método para obtener las notas de crédito asociadas a una factura
    const fetchNotasCredito = async (idFactura) => {
        if (!idFactura) return;
        
        try {
            const response = await fetch(`${server}/MgNotasCredito/PorFactura/${idFactura}`);
            
            if (response.ok) {
                const data = await response.json();
                console.log('Datos de notas de crédito recibidos:', data);
                
                // Deserializar la estructura con $values
                let notasArray = [];
                if (data && data.$values && Array.isArray(data.$values)) {
                    notasArray = data.$values;
                } else if (Array.isArray(data)) {
                    notasArray = data;
                }
                
                console.log('📋 Notas de crédito array:', notasArray);
                
                // Cargar los detalles de cada nota
                const notasConDetalles = await Promise.all(
                    notasArray.map(async (nota) => {
                        try {
                            const idNota = nota.IdNotaCredito || nota.idNotaCredito;
                            
                            if (!idNota || idNota === 0) {
                                console.error('Nota de crédito sin ID válido:', nota);
                                return nota;
                            }
                            
                            const detallesResponse = await fetch(`${server}/MgNotasCredito/${idNota}`);
                            
                            if (detallesResponse.ok) {
                                const notaCompleta = await detallesResponse.json();
                                
                                const detalles = notaCompleta.MgNotasCreditoDetalles?.$values || 
                                               notaCompleta.mgNotasCreditoDetalles?.$values || 
                                               notaCompleta.MgNotasCreditoDetalles || 
                                               notaCompleta.mgNotasCreditoDetalles || 
                                               [];
                                
                                return {
                                    ...nota,
                                    ...notaCompleta,
                                    IdNotaCredito: idNota,
                                    idNotaCredito: idNota,
                                    MgNotasCreditoDetalles: detalles,
                                    mgNotasCreditoDetalles: detalles
                                };
                            }
                            
                            return {
                                ...nota,
                                IdNotaCredito: idNota,
                                idNotaCredito: idNota,
                                MgNotasCreditoDetalles: [],
                                mgNotasCreditoDetalles: []
                            };
                        } catch (error) {
                            console.error('Error al cargar detalles de nota de crédito:', error);
                            return nota;
                        }
                    })
                );
                
                console.log('Notas de crédito procesadas:', notasConDetalles);
                setNotasCredito(notasConDetalles);
            } else if (response.status === 404) {
                console.log('No se encontraron notas de crédito para la factura');
                setNotasCredito([]);
            } else {
                console.error(`Error al obtener notas de crédito: ${response.status}`);
                setNotasCredito([]);
            }
        } catch (error) {
            console.error("Error al obtener las notas de crédito:", error);
            setNotasCredito([]);
        }
    };
    
    // Método para abrir el modal de nueva nota de crédito
    const handleNuevaNotaCredito = async (factura) => {
        try {
            // Obtener el valor total de la factura con descuento aplicado (monto neto)
            const montoBruto = parseFloat(factura.valorVenta || 0);
            const descuentoFactura = parseFloat(factura.valorDescuento || 0);
            const valorFactura = montoBruto - descuentoFactura; // Valor neto con descuento
            
            console.log('💰 Nota de Crédito - Valores:', {
                montoBruto,
                descuentoFactura,
                valorFacturaNeto: valorFactura
            });
            
            // Calcular el total devuelto en notas de crédito existentes
            const response = await fetch(`${server}/MgNotasCredito/PorFactura/${factura.idFactura}`);
            let totalDevuelto = 0;
            
            if (response.ok) {
                const data = await response.json();
                const notasArray = data?.$values || data || [];
                totalDevuelto = notasArray.reduce((acc, nota) => 
                    acc + parseFloat(nota.ValorNotaCredito || nota.valorNotaCredito || 0), 0
                );
            }
            
            const valorDisponible = valorFactura - totalDevuelto;
            
            if (valorDisponible <= 0) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Operación no permitida',
                    text: 'No hay valor disponible para devolver en esta factura'
                });
                return;
            }
            
            setNuevaNotaCreditoData({
                idCliente: idCliente,
                valorNotaCredito: 0,
                valorDisponible: valorDisponible,
                motivo: `Devolución de factura ${factura.numeroFactura}`,
                detalles: [{
                    idFactura: factura.idFactura,
                    valorOriginal: valorFactura,
                    valorDevolucion: 0
                }],
                documentosDevolucion: []
            });
            
            setOpenNuevaNotaCredito(true);
            
        } catch (error) {
            console.error('Error al preparar nota de crédito:', error);
            Swal.fire('Error', 'No se pudo preparar la nota de crédito', 'error');
        }
    };
    
    // Método para crear una nueva nota de crédito
    const handleCrearNotaCredito = async () => {
        // Validaciones
        if (!idCliente) {
            Swal.fire('Error', 'Debe seleccionar un cliente', 'error');
            return;
        }
        
        if (!nuevaNotaCreditoData.detalles || nuevaNotaCreditoData.detalles.length === 0) {
            Swal.fire('Error', 'Debe agregar al menos un detalle de devolución', 'error');
            return;
        }
        
        if (parseFloat(nuevaNotaCreditoData.valorNotaCredito || 0) <= 0) {
            Swal.fire('Error', 'El valor de la nota de crédito debe ser mayor a cero', 'error');
            return;
        }
        
        // Validar que los documentos de devolución coincidan con el valor
        const totalDocumentos = (nuevaNotaCreditoData.documentosDevolucion || []).reduce(
            (acc, doc) => acc + parseFloat(doc.valorDevolucion || 0), 
            0
        );
        const valorNota = parseFloat(nuevaNotaCreditoData.valorNotaCredito || 0);
        
        if (Math.abs(totalDocumentos - valorNota) > 0.01) {
            Swal.fire('Error', 
                `La suma de los documentos de devolución (L. ${totalDocumentos.toFixed(2)}) debe coincidir con el valor de la nota (L. ${valorNota.toFixed(2)})`, 
                'error');
            return;
        }
        
        try {
            const ahora = new Date();
            const fechaLocal = new Date(ahora.getTime() - (ahora.getTimezoneOffset() * 60000));
            
            // Preparar documentos de pago para enviar en la nota de crédito
            const documentosDevolucion = nuevaNotaCreditoData.documentosDevolucion || [];
            const idFacturaDevolucion = nuevaNotaCreditoData.detalles?.[0]?.idFactura;
            const documentosPago = cajaAbiertaDatos && idFacturaDevolucion ? documentosDevolucion
                .filter(doc => parseFloat(doc.valorDevolucion) > 0)
                .map(doc => ({
                    IdTipoTransaccion: parseInt(doc.tipoDocumento),
                    Valor: -parseFloat(doc.valorDevolucion), // VALOR NEGATIVO para devolución
                    IngresadoPor: user?.codigoUsuario || user?.nombreUsuario || 'ADMIN',
                    IdEncabezado: cajaAbiertaDatos,
                    IdFactura: idFacturaDevolucion
                })) : [];
            
            const notaCreditoData = {
                IdCliente: parseInt(idCliente),
                FechaNotaCredito: fechaLocal.toISOString().slice(0, 19),
                Anio: ahora.getFullYear(),
                Mes: ahora.getMonth() + 1,
                ValorNotaCredito: parseFloat(nuevaNotaCreditoData.valorNotaCredito) || 0,
                Motivo: nuevaNotaCreditoData.motivo || 'Devolución',
                IngresadoPor: user?.nombreUsuario || user?.codigoUsuario || 'ADMIN',
                // Detalles de la nota (relación con facturas)
                MgNotasCreditoDetalles: (nuevaNotaCreditoData.detalles || []).map(det => ({
                    IdFactura: parseInt(det.idFactura),
                    ValorOriginal: parseFloat(det.valorOriginal) || 0,
                    ValorDevolucion: parseFloat(det.valorDevolucion) || 0,
                    IngresadoPor: user?.nombreUsuario || user?.codigoUsuario || 'ADMIN'
                })),
                // Documentos de pago (movimientos de caja)
                DocumentosPago: documentosPago
            };
            
            console.log('Enviando datos de la nota de crédito:', notaCreditoData);
            
            const response = await fetch(`${server}/MgNotasCredito`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(notaCreditoData)
            });
            
            if (response.ok) {
                const notaCreada = await response.json();
                const idNota = notaCreada.IdNotaCredito || 
                              notaCreada.idNotaCredito || 
                              notaCreada.Id || 
                              notaCreada.id;
                
                if (!idNota || idNota === 0) {
                    console.error('No se pudo obtener el ID de la nota de crédito creada');
                    Swal.fire({
                        icon: 'warning',
                        title: 'Nota creada con advertencia',
                        text: 'La nota se creó pero no se pudieron registrar los movimientos de caja.',
                    });
                    return;
                }
                
                // Procesar documentos de devolución (movimientos de caja ya se enviaron en DocumentosPago)
                const documentosDevolucion = nuevaNotaCreditoData.documentosDevolucion || [];
                
                try {
                    const numeroNota = `NC${String(idNota).padStart(9, '0')}`;
                    
                    // Procesar puntos para documentos de devolución que incluyen puntos
                    for (const documento of documentosDevolucion) {
                        const valorDocumento = parseFloat(documento.valorDevolucion) || 0;
                        
                        if (valorDocumento > 0) {
                            // Si el documento es de puntos, crear el registro de DEVOLUCIÓN de puntos
                            if (documento.descripcion && documento.descripcion.toLowerCase().includes('puntos')) {
                                try {
                                    const fechaActual = new Date();
                                    const dataPuntos = {
                                        fecha: fechaActual.toISOString().split('T')[0],
                                        idTipo: 2, // Tipo 2 = DEVOLUCIÓN (en lugar de 1 = COBRO)
                                        idComercio: 1,
                                        documento: numeroNota,
                                        ingresadoPor: user?.codigoUsuario || user?.nombreUsuario || 'ADMIN',
                                    };
                                    
                                    console.log('Puntos Devolución a guardar para nota de crédito:', dataPuntos);
                                    const responsePuntos = await fetch(`${server}/MgClientesPuntosCobroes`, {
                                        method: 'POST',
                                        headers: {
                                            'Content-Type': 'application/json',
                                        },
                                        body: JSON.stringify(dataPuntos),
                                    });
                                    
                                    if (responsePuntos.ok) {
                                        const puntosData = await responsePuntos.json();
                                        const idCobro = puntosData.id || puntosData.Id;
                                        const puntos = valorDocumento / 0.30; // Conversión estándar (L. 0.30 por punto)
                                        
                                        // En devoluciones, los puntos se SUMAN al cliente (positivos)
                                        const dataPuntosDetalle = {
                                            idCobro: idCobro,
                                            idCliente: parseInt(idCliente),
                                            valorPuntos: puntos, // Puntos positivos (se suman al cliente)
                                            valorLempiras: valorDocumento, // Lempiras positivas
                                            ingresadoPor: user?.codigoUsuario || user?.nombreUsuario || 'ADMIN',
                                        };
                                        
                                        console.log('Detalle de puntos devueltos a guardar:', dataPuntosDetalle);
                                        const responsePuntosDetalle = await fetch(`${server}/MgClientesPuntosCobrosDetalles`, {
                                            method: 'POST',
                                            headers: {
                                                'Content-Type': 'application/json',
                                            },
                                            body: JSON.stringify(dataPuntosDetalle),
                                        });
                                        
                                        if (responsePuntosDetalle.ok) {
                                            console.log('✅ Puntos devueltos registrados exitosamente:', puntos, 'puntos (', valorDocumento, 'lempiras)');
                                        } else {
                                            console.error('❌ Error al guardar detalle de puntos devueltos');
                                        }
                                    }
                                } catch (errorPuntos) {
                                    console.error('Error al procesar devolución de puntos para nota de crédito:', errorPuntos);
                                }
                            }
                        }
                    }
                    
                } catch (error) {
                    console.error('Error al procesar movimientos de caja para nota de crédito:', error);
                }
                
                Swal.fire({
                    title: 'Nota de crédito creada',
                    text: `La nota de crédito NC${String(idNota).padStart(9, '0')} se ha creado correctamente`,
                    icon: 'success',
                    confirmButtonText: 'Aceptar'
                }).then(() => {
                    // Recargar notas de crédito para las facturas afectadas
                    const facturasAfectadas = nuevaNotaCreditoData.detalles.map(det => det.idFactura);
                    facturasAfectadas.forEach(idFactura => {
                        fetchNotasCredito(idFactura);
                    });
                    
                    // Cerrar el modal y limpiar datos
                    setOpenNuevaNotaCredito(false);
                    setNuevaNotaCreditoData({
                        idCliente: '',
                        valorNotaCredito: 0,
                        motivo: '',
                        detalles: [],
                        documentosDevolucion: []
                    });
                });
            } else {
                let errorMessage = 'Error desconocido';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorData.Message || JSON.stringify(errorData);
                } catch (e) {
                    errorMessage = `Error ${response.status}: ${response.statusText}`;
                }
                console.error('Error al crear nota de crédito:', errorMessage);
                Swal.fire('Error', `No se pudo crear la nota de crédito: ${errorMessage}`, 'error');
            }
        } catch (error) {
            console.error("Error al crear la nota de crédito:", error);
            Swal.fire('Error', `No se pudo crear la nota de crédito: ${error.message}`, 'error');
        }
    };
    
    // Métodos para gestionar detalles de nota de crédito
    const handleAgregarDetalleNotaCredito = () => {
        setNuevaNotaCreditoData(prev => ({
            ...prev,
            detalles: [
                ...prev.detalles,
                {
                    idFactura: '',
                    valorOriginal: 0,
                    valorDevolucion: 0
                }
            ]
        }));
    };
    
    const handleEliminarDetalleNotaCredito = (index) => {
        const nuevosDetalles = [...nuevaNotaCreditoData.detalles];
        nuevosDetalles.splice(index, 1);
        
        // Recalcular valor total
        const nuevoValor = nuevosDetalles.reduce((acc, det) => 
            acc + parseFloat(det.valorDevolucion || 0), 0
        );
        
        setNuevaNotaCreditoData(prev => ({
            ...prev,
            detalles: nuevosDetalles,
            valorNotaCredito: nuevoValor
        }));
    };
    
    const handleUpdateDetalleNotaCredito = (index, field, value) => {
        const nuevosDetalles = [...nuevaNotaCreditoData.detalles];
        nuevosDetalles[index] = {
            ...nuevosDetalles[index],
            [field]: value
        };
        
        // Recalcular valor total
        const nuevoValor = nuevosDetalles.reduce((acc, det) => 
            acc + parseFloat(det.valorDevolucion || 0), 0
        );
        
        setNuevaNotaCreditoData(prev => ({
            ...prev,
            detalles: nuevosDetalles,
            valorNotaCredito: nuevoValor
        }));
    };
    
    // Métodos para gestionar documentos de devolución
    const handleAgregarDocumentoDevolucion = () => {
        setNuevaNotaCreditoData(prev => ({
            ...prev,
            documentosDevolucion: [
                ...prev.documentosDevolucion,
                {
                    tipoDocumento: '',
                    descripcion: '',
                    valorDevolucion: 0
                }
            ]
        }));
    };
    
    const handleEliminarDocumentoDevolucion = (index) => {
        const nuevosDocumentos = [...nuevaNotaCreditoData.documentosDevolucion];
        nuevosDocumentos.splice(index, 1);
        
        setNuevaNotaCreditoData(prev => ({
            ...prev,
            documentosDevolucion: nuevosDocumentos
        }));
    };
    
    const handleUpdateDocumentoDevolucion = (index, field, value) => {
        const nuevosDocumentos = [...nuevaNotaCreditoData.documentosDevolucion];
        nuevosDocumentos[index] = {
            ...nuevosDocumentos[index],
            [field]: value
        };
        
        // Si se cambia el tipo de documento, actualizar la descripción
        if (field === 'tipoDocumento') {
            const tipoDoc = transaccionesCajero.find(t => t.id === parseInt(value));
            if (tipoDoc) {
                nuevosDocumentos[index].descripcion = tipoDoc.descripcionTipoTransaccion;
            }
        }
        
        setNuevaNotaCreditoData(prev => ({
            ...prev,
            documentosDevolucion: nuevosDocumentos
        }));
    };

    return (
        <div style={{ maxHeight: '95vh', overflowY: 'auto', overflowX: 'auto', padding: '0 15px' }}>
            <TableContainer component={Paper}  sx={{ maxWidth: "100%", overflowX: "auto" }}>
                <Table size="small" sx={{minWidth: 1200}}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Número de Factura</TableCell>
                            <TableCell>Cliente</TableCell>
                            <TableCell>Tipo de Pago</TableCell>
                            <TableCell>Monto Total</TableCell>
                            {/* Solo mostrar columnas de crédito si hay al menos una factura a crédito */}
                            {facturas && facturas.some(factura => factura?.idFormaPago === 2) && (
                                <>
                                    <TableCell>Total Abonado</TableCell>
                                    <TableCell>Saldo Pendiente</TableCell>
                                </>
                            )}
                            <TableCell>Acciones</TableCell>
                            <TableCell>Estado</TableCell>
                            <TableCell>Fecha modificacion</TableCell>
                            <TableCell>Usuario Modificacion</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {facturas && facturas.length > 0 ? (
                            facturas.map((factura, index) => {
                                if (!factura) return null; // Omitir si factura es null o undefined
                                
                                // Calcular total abonado SOLO de los recibos que pertenecen a esta factura específica
                                console.log(`Factura ${factura.numeroFactura} (ID: ${factura.idFactura})`);
                                console.log('Recibos disponibles:', recibos);
                                
                                const recibosFiltrados = recibos.filter(recibo => {
                                    // Verificar si este recibo tiene detalles para esta factura
                                    const detalles = recibo.MgRecibosDetalles || recibo.mgRecibosDetalles || [];
                                    console.log(`  Recibo ${recibo.IdRecibo || recibo.idRecibo}, detalles:`, detalles);
                                    
                                    const pertenece = detalles.some(det => {
                                        const match = (det.IdFactura === factura.idFactura) || (det.idFactura === factura.idFactura);
                                        console.log(`    Detalle IdFactura: ${det.IdFactura || det.idFactura}, match: ${match}`);
                                        return match;
                                    });
                                    console.log(`  Recibo pertenece a esta factura: ${pertenece}`);
                                    return pertenece;
                                });
                                
                                console.log(`Recibos filtrados para factura ${factura.numeroFactura}:`, recibosFiltrados);
                                
                                const totalAbonado = recibosFiltrados.reduce((total, recibo) => {
                                    const valorRecibo = recibo.ValorRecibo || recibo.valorRecibo || 0;
                                    return total + parseFloat(valorRecibo);
                                }, 0);
                                
                                console.log(`Total abonado para factura ${factura.numeroFactura}: ${totalAbonado}`);
                                
                                const montoBruto = parseFloat(factura.valorVenta || 0);
                                const descuentoFactura = parseFloat(factura.valorDescuento || 0);
                                const puntos = parseFloat(factura.lempirasLacm || 0);
                                const montoTotal = montoBruto - descuentoFactura - puntos; // MONTO NETO
                                const saldoPendiente = montoTotal - totalAbonado;
                                
                                console.log(`🧾 Factura ${factura.numeroFactura}:`, {
                                    montoBruto,
                                    descuentoFactura,
                                    montoTotal,
                                    totalAbonado,
                                    saldoPendiente
                                });
                                
                                return (
                                    <TableRow key={factura.idFactura || index}>
                                        <TableCell>{factura.numeroFactura}</TableCell>
                                        <TableCell>{factura.nombreCliente || 'N/A'}</TableCell>
                                        <TableCell>
                                            <span style={{
                                                backgroundColor: factura.idFormaPago === 2 ? '#fff3cd' : '#d1ecf1',
                                                color: factura.idFormaPago === 2 ? '#856404' : '#0c5460',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                fontWeight: 'bold',
                                                fontSize: '0.85em'
                                            }}>
                                                {factura.idFormaPago === 2 ? 'CRÉDITO' : 'CONTADO'}
                                            </span>
                                        </TableCell>
                                        <TableCell>L. {montoTotal.toFixed(2)}</TableCell>
                                        {/* Solo mostrar columnas de crédito si hay al menos una factura a crédito */}
                                        {facturas && facturas.some(f => f?.idFormaPago === 2) && (
                                            <>
                                                <TableCell>L. {totalAbonado.toFixed(2)}</TableCell>
                                                <TableCell>
                                                    <span style={{ 
                                                        color: saldoPendiente <= 0 ? 'green' : 'orange',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        L. {saldoPendiente.toFixed(2)}
                                                        {saldoPendiente <= 0 && ' (PAGADA)'}
                                                    </span>
                                                </TableCell>
                                            </>
                                        )}
                                        <TableCell>
                                            <Button onClick={() => handleFacturaPreview(factura.idFactura, 'Carta')} size="small" disabled={!tienePermiso('Imprimir Factura')}>
                                                <PrintTwoTone /> Carta
                                            </Button>
                                            <Button onClick={() => handleFacturaPreview(factura.idFactura, 'Ticket')} size="small" disabled={!tienePermiso('Imprimir Factura')}>
                                                <PrintOutlined /> Ticket
                                            </Button>
                                            <Button onClick={() => handleWhatsapp(factura.idFactura)} disabled={!tienePermiso('Imprimir Factura')}>
                                                <WhatsApp /> WhatsApp
                                            </Button>
                                            <Button onClick={() => handleEnviarCorreo(factura.idFactura)} disabled={!tienePermiso('Imprimir Factura')}>
                                                <Email /> Correo
                                            </Button>
                                            <Button 
                                                onClick={() => handleVerDetallesPago(factura)} 
                                                color="info" 
                                                size="small"
                                                title="Ver detalles de tipos de transacciones/pagos"
                                            >
                                                <Info /> Detalles
                                            </Button>
                                            {factura.idFormaPago === 2 && saldoPendiente > 0 && (
                                                <Button 
                                                    onClick={() => handleNuevoRecibo(factura)} 
                                                    color="primary" 
                                                    size="small"
                                                    title="Crear recibo de pago para factura a crédito"
                                                    disabled={!tienePermiso('Crear Recibo de Pago')}
                                                >
                                                    <Add /> Recibo de Pago
                                                </Button>
                                            )}
                                            <Button 
                                                onClick={() => handleNuevaNotaCredito(factura)} 
                                                color="warning" 
                                                size="small"
                                                title="Crear nota de crédito para devolución"
                                                disabled={!tienePermiso('Crear Nota de Crédito')}
                                            >
                                                <Remove /> Nota de Crédito
                                            </Button>
                                        </TableCell>
                                        <TableCell>{factura.estado}</TableCell>
                                        <TableCell> {factura.modificacion === 1
                                            ? factura.fechaIngreso
                                            : factura.modificacion === 2
                                            ? factura.fechaModificacion
                                            : ""}
                                        </TableCell>
                                        <TableCell>
                                            {factura.modificacion === 1
                                            ? factura.ingresadoPor
                                            : factura.modificacion === 2
                                            ? factura.modificadoPor
                                            : ""}
                                        </TableCell>
                                        
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No hay facturas disponibles.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                
                {/* Botón para crear recibo general si hay múltiples facturas a crédito */}
                {facturas && facturas.filter(f => f?.idFormaPago === 2).length > 1 && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            startIcon={<Add />}
                            onClick={() => handleNuevoReciboGeneral()}
                            size="small"
                        >
                            Crear Recibo General (Múltiples Facturas)
                        </Button>
                    </Box>
                )}
                
                {/* Solo mostrar la tabla de recibos si hay facturas a crédito */}
                {facturas && facturas.some(factura => factura?.idFormaPago === 2) && (
                    <>
                        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                            Recibos de Pago Asociados a Facturas de Crédito
                        </Typography>
                        
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Número de Recibo</TableCell>
                                    <TableCell>Cliente</TableCell>
                                    <TableCell>Fecha</TableCell>
                                    <TableCell>Concepto</TableCell>
                                    <TableCell>Saldo Anterior</TableCell>
                                    <TableCell>Abono</TableCell>
                                    <TableCell>Saldo Final</TableCell>
                                    <TableCell>Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {recibos && recibos.length > 0 ? (
                                    recibos.map((recibo, index) => {
                                        if (!recibo) return null;
                                        
                                        // Debug: mostrar todas las propiedades del recibo
                                        console.log('Recibo en tabla:', {
                                            IdRecibo: recibo.IdRecibo,
                                            idRecibo: recibo.idRecibo,
                                            todasLasPropiedades: Object.keys(recibo)
                                        });
                                        
                                        return (
                                            <TableRow key={recibo.IdRecibo || recibo.idRecibo || index}>
                                                <TableCell>C{String(recibo.IdRecibo || recibo.idRecibo || 0).padStart(9, '0')}</TableCell>
                                                <TableCell>{recibo.IdClienteNavigation?.NombreCompleto || recibo.idClienteNavigation?.nombreCompleto}</TableCell>
                                                <TableCell>{new Date(recibo.FechaRecibo || recibo.fechaRecibo).toLocaleDateString()}</TableCell>
                                                <TableCell>{recibo.Concepto || recibo.concepto || 'Abono a factura'}</TableCell>
                                                <TableCell>L. {parseFloat(recibo.SaldoAnterior || recibo.saldoAnterior || 0).toFixed(2)}</TableCell>
                                                <TableCell>L. {parseFloat(recibo.ValorRecibo || recibo.valorRecibo).toFixed(2)}</TableCell>
                                                <TableCell>L. {parseFloat(recibo.SaldoActual || recibo.saldoActual || 0).toFixed(2)}</TableCell>
                                                <TableCell>
                                                    <Button 
                                                        onClick={() => {
                                                            console.log('Recibo completo:', recibo);
                                                            const reciboId = recibo.IdRecibo || recibo.idRecibo;
                                                            console.log('ID extraído:', reciboId, 'Tipo:', typeof reciboId);
                                                            
                                                            // Convertir a número para validar correctamente
                                                            const numericId = parseInt(reciboId);
                                                            console.log('ID numérico:', numericId);
                                                            
                                                            if (!reciboId || numericId === 0 || isNaN(numericId)) {
                                                                Swal.fire({
                                                                    icon: 'warning',
                                                                    title: 'Recibo sin ID válido',
                                                                    text: `Este recibo no tiene un ID válido para imprimir (ID: ${reciboId}). Puede ser que aún no se haya guardado correctamente.`,
                                                                });
                                                                return;
                                                            }
                                                            handleImprimirRecibo(numericId);
                                                        }} 
                                                        size="small"
                                                    >
                                                        <PrintTwoTone /> Imprimir
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            No hay recibos asociados a esta factura.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        
                        {/* Resumen de recibos */}
                        {recibos && recibos.length > 0 && (
                            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                                Total de recibos: {recibos.length} | 
                                Total abonado: L. {recibos.reduce((total, recibo) => total + parseFloat(recibo.ValorRecibo || recibo.valorRecibo || 0), 0).toFixed(2)}
                            </Typography>
                        )}
                    </>
                )}
                
                {/* Tabla de Notas de Crédito */}
                {facturas && facturas.length > 0 && (
                    <>
                        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                            Notas de Crédito (Devoluciones)
                        </Typography>
                        
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Número de Nota</TableCell>
                                    <TableCell>Cliente</TableCell>
                                    <TableCell>Fecha</TableCell>
                                    <TableCell>Motivo</TableCell>
                                    <TableCell>Valor Devuelto</TableCell>
                                    <TableCell>Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {notasCredito && notasCredito.length > 0 ? (
                                    notasCredito.map((nota, index) => {
                                        if (!nota) return null;
                                        
                                        return (
                                            <TableRow key={nota.IdNotaCredito || nota.idNotaCredito || index}>
                                                <TableCell>NC{String(nota.IdNotaCredito || nota.idNotaCredito || 0).padStart(9, '0')}</TableCell>
                                                <TableCell>{nota.IdClienteNavigation?.NombreCompleto || nota.idClienteNavigation?.nombreCompleto || nombreCliente}</TableCell>
                                                <TableCell>{new Date(nota.FechaNotaCredito || nota.fechaNotaCredito).toLocaleDateString()}</TableCell>
                                                <TableCell>{nota.Motivo || nota.motivo || 'Devolución'}</TableCell>
                                                <TableCell style={{ color: 'red', fontWeight: 'bold' }}>
                                                    -L. {parseFloat(nota.ValorNotaCredito || nota.valorNotaCredito).toFixed(2)}
                                                </TableCell>
                                                <TableCell>
                                                    <Button 
                                                        onClick={() => {
                                                            Swal.fire({
                                                                title: 'Detalles de Nota de Crédito',
                                                                html: `
                                                                    <div style="text-align: left;">
                                                                        <p><strong>Número:</strong> NC${String(nota.IdNotaCredito || nota.idNotaCredito || 0).padStart(9, '0')}</p>
                                                                        <p><strong>Fecha:</strong> ${new Date(nota.FechaNotaCredito || nota.fechaNotaCredito).toLocaleString()}</p>
                                                                        <p><strong>Motivo:</strong> ${nota.Motivo || nota.motivo || 'Devolución'}</p>
                                                                        <p><strong>Valor:</strong> L. ${parseFloat(nota.ValorNotaCredito || nota.valorNotaCredito).toFixed(2)}</p>
                                                                        <p><strong>Ingresado por:</strong> ${nota.IngresadoPor || nota.ingresadoPor || 'N/A'}</p>
                                                                    </div>
                                                                `,
                                                                icon: 'info'
                                                            });
                                                        }} 
                                                        size="small"
                                                        color="info"
                                                    >
                                                        <Visibility /> Ver Detalles
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            No hay notas de crédito asociadas a estas facturas.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        
                        {/* Resumen de notas de crédito */}
                        {notasCredito && notasCredito.length > 0 && (
                            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'red' }}>
                                Total de notas: {notasCredito.length} | 
                                Total devuelto: -L. {notasCredito.reduce((total, nota) => total + parseFloat(nota.ValorNotaCredito || nota.valorNotaCredito || 0), 0).toFixed(2)}
                            </Typography>
                        )}
                    </>
                )}
                <Grid item xs={12}>
                    <hr />
                </Grid>
            </TableContainer>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <hr />
                </Grid>
                {/* Sección: Orden de Trabajo, Nombre del Cliente, Identidad */}
                <Grid item xs={12} md={8}>
                    {ordenesSeleccionadas.length > 1 && (
                        <Paper variant="outlined" sx={{ mb: 2, p: 1 }}>
                            <Typography variant="subtitle1">Órdenes a facturar:</Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell># Orden</TableCell>
                                        <TableCell>Cliente</TableCell>
                                        <TableCell align="right">Valor</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {ordenesSeleccionadas.map((o, i) =>
                                        <TableRow key={i}>
                                            <TableCell>{o.idOrdenMedica || o.ordenTrabajo}</TableCell>
                                            <TableCell>{o.nombreCompleto || o.nombreCliente}</TableCell>
                                            <TableCell align="right">
                                                {(parseFloat(o.valorPagar || o.valorVenta) || 0).toFixed(2)}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    <TableRow>
                                        <TableCell colSpan={2}><strong>Total</strong></TableCell>
                                        <TableCell align="right">
                                            <strong>L. {totalOrdenes.toFixed(2)}</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Paper>
                    )}
                </Grid>

                {dividirFactura ? (
                    <Grid container spacing={2}>
                        {/* Formulario para Cliente Principal */}
                        <Grid item xs={12} md={6}>
                            <FacturaFormSection
                                suffix="Cliente Principal"
                                isSecondary={false}
                                cliente={{ nombreCompleto: nombreCliente, identidad, idCliente }}
                                onClienteChange={c => {
                                    setNombreCliente(c?.nombreCompleto || '')
                                    setIdentidad(c?.identidad || '')
                                    setIdCliente(c?.idCliente || '')
                                }}
                                // identidad={identidadPrincipal}
                                puntosActuales={puntosActuales}
                                lempirasActuales={lempirasActuales}
                                tipoDocumento={tipoDocPrincipal}
                                onTipoDocumentoChange={setTipoDocPrincipal}
                                numero={numPrincipal}
                                onNumeroChange={setNumPrincipal}
                                valorPagado={valorPagadoPrincipal}
                                onValorPagadoChange={setValorPagadoPrincipal}
                                documentos={docPrincipal}
                                onAgregarDocumento={() => addDocumento({
                                    tipoDocumento: tipoDocPrincipal,
                                    valorPagado: valorPagadoPrincipal,
                                    lempirasActuales: lempirasPrincipal,
                                    setLempirasActuales: setLempirasPrincipal,
                                    documentos: docPrincipal,
                                    setDocumentos: setDocPrincipal,
                                    numero: numPrincipal
                                })}
                                onEliminarDocumento={idx => removeDocumento({
                                    index: idx,
                                    documentos: docPrincipal,
                                    setDocumentos: setDocPrincipal,
                                    lempirasActuales: lempirasPrincipal,
                                    setLempirasActuales: setLempirasPrincipal
                                })}
                                title="Cliente Principal"
                                clientes={clientes}
                                isLoading={isLoading}
                                clienteOrigenChecked={clienteOrigenCheckedPr}
                                onClienteOrigenCheckedChange={setClienteOrigenCheckedPr}
                                montoConfirmado={montoConfirmadoPr}
                                onMontoConfirmadoChange={setMontoConfirmadoPr}
                                tipoFactura={tipoFacturaPr}
                                onTipoFacturaChange={setTipoFacturaPr}
                                ordenCompra={ordenCompraPr}
                                onOrdenCompraChange={setOrdenCompraPr}
                                ordenCompraExcenta={ordenCompraExcentaPr}
                                onOrdenCompraExcentaChange={setOrdenCompraExcentaPr}
                                constanciaRegistro={constanciaRegistroPr}
                                onConstanciaRegistroChange={setConstanciaRegistroPr}
                                registroSAG={registroSAGPr}
                                onRegistroSAGChange={setRegistroSAGPr}
                                tipoComprobante={tipoComprobantePr}
                                onTipoComprobanteChange={setTipoComprobantePr}
                                dividirFactura={dividirFactura}
                                onDividirFacturaChange={setDividirFactura}
                                showPolizaOptions={showPolizaOptions}
                                clienteSecundario={clienteSecundario}
                                onClienteSecundarioChange={setClienteSecundario}
                                origenes={origenes}
                                origen={origen}
                                onOrigenChange={setOrigen}
                                polizaSplitOption={polizaSplitOption}
                                onPolizaSplitOptionChange={setPolizaSplitOption}
                                porcentajeCliente={porcentajeClientePrincipal}
                                onPorcentajeClienteChange={v => { setCampoEditado('porcentajePrincipal'); setPorcentajeClientePrincipal(v) }}
                                valorPagar={montoClientePrincipal}
                                onValorPagarChange={setMontoClientePrincipal}
                                montoPendiente={montoPendiente}
                                cambio={cambioPrincipal}
                                transaccionesCajero={transaccionesCajero}
                                despachando={despachando}
                                loading={despachando}
                                onCancel={handleCancelar}
                                onSubmit={handleDespachar}
                                porcentajeCopago={porcentajeCopago}
                                porcentajeAseguradora={porcentajeAseguradora}
                                diasCredito={diasCreditoPr}
                                onDiasCreditoChange={setDiasCreditoPr}
                                disabledDespachar={!tienePermiso('Despachar Factura') || !cajaAbierta}
                            />
                        </Grid>

                        {/* Formulario para Cliente Secundario */}
                        <Grid item xs={12} md={6}>
                            <FacturaFormSection
                                suffix="Cliente Secundario"
                                isSecondary={true}
                                cliente={clienteSecundario}
                                onClienteChange={setClienteSecundario}
                                identidad={identidadSec}
                                puntosActuales={puntosActualesSec}
                                lempirasActuales={lempirasSecundario}
                                clientes={clientes}
                                isLoading={isLoading}
                                clienteSecundario={clienteSecundario}
                                onClienteSecundarioChange={setClienteSecundario}
                                identidadSec={identidadSec}
                                puntosActualesSec={puntosActualesSec}
                                lempirasActualesSec={lempirasSecundario}
                                onSearchClientes={fetchClientes}
                                tipoDocumento={tipoDocSecundario}
                                onTipoDocumentoChange={setTipoDocSecundario}
                                numero={numSecundario}
                                onNumeroChange={setNumSecundario}
                                valorPagado={valorPagadoSecundario}
                                onValorPagadoChange={setValorPagadoSecundario}
                                documentos={docSecundario}
                                onAgregarDocumento={() => addDocumento({
                                    tipoDocumento: tipoDocSecundario,
                                    valorPagado: valorPagadoSecundario,
                                    lempirasActuales: lempirasSecundario,
                                    setLempirasActuales: setLempirasSecundario,
                                    documentos: docSecundario,
                                    setDocumentos: setDocSecundario,
                                    numero: numSecundario
                                })}
                                onEliminarDocumento={idx => removeDocumento({
                                    index: idx,
                                    documentos: docSecundario,
                                    setDocumentos: setDocSecundario,
                                    lempirasActuales: lempirasSecundario,
                                    setLempirasActuales: setLempirasSecundario
                                })}
                                title="Cliente Secundario"
                                clienteOrigenChecked={clienteOrigenCheckedSec}
                                onClienteOrigenCheckedChange={setClienteOrigenCheckedSec}
                                montoConfirmado={montoConfirmadoSec}
                                onMontoConfirmadoChange={setMontoConfirmadoSec}
                                tipoFactura={tipoFacturaSec}
                                onTipoFacturaChange={setTipoFacturaSec}
                                ordenCompra={ordenCompraSec}
                                onOrdenCompraChange={setOrdenCompraSec}
                                ordenCompraExcenta={ordenCompraExcentaSec}
                                onOrdenCompraExcentaChange={setOrdenCompraExcentaSec}
                                constanciaRegistro={constanciaRegistroSec}
                                onConstanciaRegistroChange={setConstanciaRegistroSec}
                                registroSAG={registroSAGSec}
                                onRegistroSAGChange={setRegistroSAGSec}
                                tipoComprobante={tipoComprobanteSec}
                                onTipoComprobanteChange={setTipoComprobanteSec}
                                dividirFactura={dividirFactura}
                                onDividirFacturaChange={setDividirFactura}
                                showPolizaOptions={showPolizaOptions}
                                origenes={origenes}
                                origen={origen}
                                onOrigenChange={setOrigen}
                                polizaSplitOption={polizaSplitOption}
                                onPolizaSplitOptionChange={setPolizaSplitOption}
                                porcentajeCliente={porcentajeClienteSecundario}
                                onPorcentajeClienteChange={v => { setCampoEditado('porcentajeSecundario'); setPorcentajeClienteSecundario(v) }}
                                valorPagar={montoClienteSecundario}
                                onValorPagarChange={setMontoClienteSecundario}
                                montoPendiente={montoPendiente}
                                cambio={cambioSecundario}
                                transaccionesCajero={transaccionesCajero}
                                despachando={despachando}
                                loading={despachando}
                                onCancel={handleCancelar}
                                onSubmit={handleDespachar}
                                porcentajeCopago={porcentajeCopago}
                                porcentajeAseguradora={porcentajeAseguradora}
                                diasCredito={diasCreditoSec}
                                onDiasCreditoChange={setDiasCreditoSec}
                                disabledDespachar={!tienePermiso('Despachar Factura') || !cajaAbierta}
                            />
                        </Grid>
                    </Grid>
                ) : (
                    <FacturaFormSection
                        title="Factura"
                        clientes={clientes}
                        isLoading={isLoading}
                        cliente={{ nombreCompleto: nombreCliente, identidad, idCliente }}
                        onClienteChange={c => {
                            setNombreCliente(c?.nombreCompleto || '')
                            setIdentidad(c?.identidad || '')
                            setIdCliente(c?.idCliente || '')
                        }}
                        puntosActuales={puntosActuales}
                        lempirasActuales={lempirasActuales}
                        dividirFactura={dividirFactura}
                        onDividirFacturaChange={setDividirFactura}
                        showPolizaOptions={showPolizaOptions}
                        clienteSecundario={clienteSecundario}
                        onClienteSecundarioChange={setClienteSecundario}
                        clienteOrigenChecked={clienteOrigenChecked}
                        onClienteOrigenCheckedChange={setClienteOrigenChecked}
                        origenes={origenes}
                        origen={origen}
                        onOrigenChange={setOrigen}
                        polizaSplitOption={polizaSplitOption}
                        onPolizaSplitOptionChange={setPolizaSplitOption}
                        porcentajeCliente={100}        // en único formulario siempre 100%
                        onPorcentajeClienteChange={() => { }}
                        valorPagar={valorPagar}
                        onValorPagarChange={setValorPagar}
                        montoPendiente={montoPendiente}
                        montoConfirmado={montoConfirmado}
                        onMontoConfirmadoChange={setMontoConfirmado}
                        tipoFactura={tipoFactura}
                        onTipoFacturaChange={setTipoFactura}
                        ordenCompra={ordenCompra}
                        onOrdenCompraChange={setOrdenCompra}
                        ordenCompraExcenta={ordenCompraExcenta}
                        onOrdenCompraExcentaChange={setOrdenCompraExcenta}
                        constanciaRegistro={constanciaRegistro}
                        onConstanciaRegistroChange={setConstanciaRegistro}
                        registroSAG={registroSAG}
                        onRegistroSAGChange={setRegistroSAG}
                        transaccionesCajero={transaccionesCajero}
                        tipoDocumento={tipoDocumento}
                        onTipoDocumentoChange={setTipoDocumento}
                        numero={numero}
                        onNumeroChange={setNumero}
                        valorPagado={valorPagado}
                        onValorPagadoChange={setValorPagado}
                        documentos={documentos}
                        onAgregarDocumento={handleAgregarDocumento}
                        onEliminarDocumento={handleEliminarDocumento}
                        cambio={cambio}
                        tipoComprobante={tipoComprobante}
                        onTipoComprobanteChange={setTipoComprobante}
                        despachando={despachando}
                        loading={despachando}
                        onCancel={handleCancelar}
                        onSubmit={handleDespachar}
                        diasCredito={diasCredito}
                        onDiasCreditoChange={setDiasCredito}
                        disabledDespachar={!tienePermiso('Despachar Factura') || !cajaAbierta}
                    />
                )}


            </Grid>

            {/* Modal de vista previa de factura */}
            <Modal
                open={openFacturaPreview}
                onClose={handleCloseFacturaPreview}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Box sx={{
                    // position: 'absolute',
                    // top: '5%',
                    // left: '50%',
                    // transform: 'translateX(-50%)',
                    // width: '95%',
                    // maxWidth: 1400,
                    // height: '90vh',
                    // bgcolor: 'background.paper',
                    // boxShadow: 24,
                    // p: 2,
                    // borderRadius: 2,
                    // overflow: 'hidden',
                    // display: 'flex',
                    // flexDirection: 'column',
                    position: 'fixed',
                    inset: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 4, // borde exterior
                }}>          
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',

                        // 👉 este es el borde interior que estabas pidiendo:
                        p: 3,  // separa el contenido del borde del modal
                    }}
                >
                          <Typography id="modal-title" variant="h6" component="h2" gutterBottom sx={{ mb: 1, flexShrink: 0 }}>
                        Vista Previa de Factura ({facturaPreviewFormato})
                    </Typography>

                    {facturaPreviewLoading && (
                        <Box display="flex" justifyContent="center" alignItems="center" sx={{ flexGrow: 1, minHeight: 400 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {facturaPdfUrl && (
                        <Box sx={{ flexGrow: 1, minHeight: 0, height: 'calc(90vh - 120px)' }}>
                            <iframe
                                src={facturaPdfUrl}
                                width="100%"
                                height="100%"
                                style={{ border: 'none', minHeight: '700px' }}
                                title="Vista Previa de Factura"
                            />
                        </Box>
                    )}

                    {facturaPreviewError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {facturaPreviewError}
                        </Alert>
                    )}                    <Box display="flex" justifyContent="flex-end" mt={1} sx={{ flexShrink: 0 }}>
                        {facturaRecienCreada && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleConfirmarYDespachar}
                                sx={{ mr: 1 }}
                                disabled={!tienePermiso('Despachar Factura') || !cajaAbierta}
                            >
                                Confirmar y Despachar
                            </Button>
                        )}
                        <Button variant="outlined" color="secondary" onClick={handleCancelarPopup}>
                            Cerrar
                        </Button>
                    </Box>
                </Box>
                </Box>
            </Modal>
            
            {/* Modal para crear nuevo recibo */}
            <Modal
                open={openNuevoRecibo}
                onClose={() => setOpenNuevoRecibo(false)}
                aria-labelledby="modal-title-recibo"
                aria-describedby="modal-description-recibo"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80%',
                    maxWidth: 800,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                    maxHeight: '90vh',
                    overflowY: 'auto'
                }}>
                    <Typography id="modal-title-recibo" variant="h6" component="h2" gutterBottom>
                        Crear Nuevo Recibo
                    </Typography>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Cliente"
                                value={nombreCliente}
                                disabled
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Identidad"
                                value={identidad}
                                disabled
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Saldo Anterior"
                                value={roundAmount(nuevoReciboData.saldoAnterior)}
                                type="number"
                                onChange={(e) => setNuevoReciboData({
                                    ...nuevoReciboData,
                                    saldoAnterior: roundAmount(e.target.value),
                                    saldoActual: roundAmount((e.target.value || 0) - (nuevoReciboData.valorRecibo || 0))
                                })}
                                margin="normal"
                                inputProps={{
                                    sx: {
                                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                            display: 'none'
                                        },
                                        '& input[type=number]': {
                                            MozAppearance: 'textfield'
                                        }
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Valor Recibo (Calculado)"
                                value={roundAmount(nuevoReciboData.valorRecibo)}
                                type="number"
                                disabled
                                helperText="Este valor se calcula automáticamente desde los detalles de abono"
                                margin="normal"
                                InputProps={{
                                    style: { backgroundColor: '#f5f5f5' }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Saldo Final"
                                value={roundAmount(nuevoReciboData.saldoActual)}
                                disabled
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Concepto"
                                value={nuevoReciboData.concepto}
                                onChange={(e) => setNuevoReciboData({
                                    ...nuevoReciboData,
                                    concepto: e.target.value
                                })}
                                margin="normal"
                            />
                        </Grid>
                        
                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h6">Detalles de Abono</Typography>
                                <Button 
                                    variant="contained" 
                                    color="primary" 
                                    startIcon={<Add />}
                                    onClick={handleAgregarDetalleRecibo}
                                >
                                    Agregar Detalle
                                </Button>
                            </Box>
                            
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Saldo Inicial</TableCell>
                                            <TableCell>Abono</TableCell>
                                            <TableCell>Saldo Final</TableCell>
                                            <TableCell>Acciones</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {nuevoReciboData.detalles.map((detalle, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={roundAmount(detalle.saldoInicial)}
                                                        onChange={(e) => handleUpdateDetalleRecibo(index, 'saldoInicial', e.target.value)}
                                                        inputProps={{
                                                            sx: {
                                                                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                                                    display: 'none'
                                                                },
                                                                '& input[type=number]': {
                                                                    MozAppearance: 'textfield'
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={toCents(detalle.abono) === 0 ? "" : roundAmount(detalle.abono)}
                                                        onChange={(e) => {
                                                            const inputValue = e.target.value;

                                                            const newValue = inputValue === "" ? 0 : Number(inputValue);
                                                            handleUpdateDetalleRecibo(index, 'abono', newValue);
                                                        }}
                                                        inputProps={{
                                                            sx: {
                                                                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                                                    display: 'none'
                                                                },
                                                                '& input[type=number]': {
                                                                    MozAppearance: 'textfield'
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {roundAmount(detalle.saldoFinal || ((detalle.saldoInicial || 0) - (detalle.abono || 0)))}
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton onClick={() => handleEliminarDetalleRecibo(index)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {nuevoReciboData.detalles.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">
                                                    No hay detalles. Agregue al menos un detalle para continuar.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                        
                        {/* Documentos de pago */}
                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} mt={3}>
                                <Typography variant="h6">Documentos de Pago</Typography>
                                <Button 
                                    variant="contained" 
                                    color="primary" 
                                    startIcon={<Add />}
                                    onClick={handleAgregarDocumentoPago}
                                >
                                    Agregar Documento
                                </Button>
                            </Box>
                            
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Tipo de Documento</TableCell>
                                            <TableCell>Número</TableCell>
                                            <TableCell>Valor</TableCell>
                                            <TableCell>Acciones</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(nuevoReciboData.documentosPago || []).map((doc, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    <Autocomplete
                                                        size="small"
                                                        options={transaccionesCajero.filter(t => t.descripcionTipoTransaccion !== 'PUNTOS LACM')}
                                                        getOptionLabel={opt => opt.descripcionTipoTransaccion || ''}
                                                        value={transaccionesCajero.find(i => i.id === doc.tipoDocumento) || null}
                                                        onChange={(e, v) => handleUpdateDocumentoPago(index, 'tipoDocumento', v?.id)}
                                                        renderInput={params => <TextField {...params} size="small" />}
                                                        style={{ width: 200 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        value={doc.numero}
                                                        onChange={(e) => handleUpdateDocumentoPago(index, 'numero', e.target.value)}
                                                        style={{ width: 120 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={toCents(doc.valorPagado) === 0 ? "" : roundAmount(doc.valorPagado)}
                                                        onChange={(e) => {
                                                            const inputValue = e.target.value;
                                                            const newValue = inputValue === "" ? 0 : Number(inputValue);
                                                            handleUpdateDocumentoPago(index, 'valorPagado', newValue);
                                                        }}
                                                        style={{ width: 100 }}
                                                        inputProps={{
                                                            sx: {
                                                                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                                                    display: 'none'
                                                                },
                                                                '& input[type=number]': {
                                                                    MozAppearance: 'textfield'
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton onClick={() => handleEliminarDocumentoPago(index)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {(!nuevoReciboData.documentosPago || nuevoReciboData.documentosPago.length === 0) && (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">
                                                    No hay documentos de pago. Agregue los métodos de pago utilizados.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            
                            {/* Resumen y validación de documentos de pago */}
                            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            <strong>Valor del Recibo:</strong>
                                        </Typography>
                                        <Typography variant="h6" color="primary">
                                            L. {parseFloat(nuevoReciboData.valorRecibo || 0).toFixed(2)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            <strong>Total Documentos:</strong>
                                        </Typography>
                                        <Typography 
                                            variant="h6" 
                                            color={
                                                (() => {
                                                    const totalDocsCents = (nuevoReciboData.documentosPago || []).reduce(
                                                        (acc, doc) => acc + toCents(doc.valorPagado || 0),
                                                        0
                                                    );
                                                    const valorReciboCents = toCents(nuevoReciboData.valorRecibo || 0);
                                                    if (totalDocsCents === valorReciboCents) return 'success.main';
                                                    if (totalDocsCents > valorReciboCents) return 'error.main';
                                                    return 'warning.main';
                                                })()
                                            }
                                        >
                                            L. {
                                                sumAmounts((nuevoReciboData.documentosPago || []).map(doc => doc.valorPagado || 0)).toFixed(2)
                                            }
                                        </Typography>
                                    </Grid>
                                </Grid>
                                
                                {/* Alertas de validación */}
                                {(() => {
                                    const totalDocsCents = (nuevoReciboData.documentosPago || []).reduce(
                                        (acc, doc) => acc + toCents(doc.valorPagado || 0),
                                        0
                                    );
                                    const valorReciboCents = toCents(nuevoReciboData.valorRecibo || 0);
                                    const totalDocs = centsToAmount(totalDocsCents);
                                    const valorRecibo = centsToAmount(valorReciboCents);
                                    
                                    if (totalDocsCents > valorReciboCents) {
                                        return (
                                            <Alert severity="error" sx={{ mt: 1 }}>
                                                Los documentos de pago exceden el valor del recibo por L. {(totalDocs - valorRecibo).toFixed(2)}
                                            </Alert>
                                        );
                                    }
                                    
                                    if (totalDocsCents < valorReciboCents && totalDocsCents > 0) {
                                        return (
                                            <Alert severity="warning" sx={{ mt: 1 }}>
                                                Faltan L. {(valorRecibo - totalDocs).toFixed(2)} en documentos de pago
                                            </Alert>
                                        );
                                    }
                                    
                                    if (totalDocsCents === valorReciboCents && valorReciboCents > 0) {
                                        return (
                                            <Alert severity="success" sx={{ mt: 1 }}>
                                                ✓ Los documentos de pago coinciden con el valor del recibo
                                            </Alert>
                                        );
                                    }
                                    
                                    return null;
                                })()}
                            </Box>
                        </Grid>
                    </Grid>
                    
                    <Box display="flex" justifyContent="flex-end" mt={3} gap={1}>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={handleCrearRecibo}
                            disabled={
                                nuevoReciboData.detalles.length === 0 || 
                                (() => {
                                    const totalDocsCents = (nuevoReciboData.documentosPago || []).reduce(
                                        (acc, doc) => acc + toCents(doc.valorPagado || 0),
                                        0
                                    );
                                    const valorReciboCents = toCents(nuevoReciboData.valorRecibo || 0);
                                    return totalDocsCents !== valorReciboCents || valorReciboCents <= 0;
                                })()
                            }
                        >
                            Crear Recibo
                        </Button>
                        <Button 
                            variant="outlined" 
                            color="secondary" 
                            onClick={() => {
                                setOpenNuevoRecibo(false);
                                setNuevoReciboData({
                                    idCliente: '',
                                    valorRecibo: 0,
                                    saldoAnterior: 0,
                                    saldoActual: 0,
                                    concepto: '',
                                    detalles: [],
                                    documentosPago: []
                                });
                            }}
                        >
                            Cancelar
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Modal para crear nueva nota de crédito */}
            <Modal
                open={openNuevaNotaCredito}
                onClose={() => setOpenNuevaNotaCredito(false)}
                aria-labelledby="modal-title-nota-credito"
                aria-describedby="modal-description-nota-credito"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80%',
                    maxWidth: 800,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                    maxHeight: '90vh',
                    overflowY: 'auto'
                }}>
                    <Typography id="modal-title-nota-credito" variant="h6" component="h2" gutterBottom>
                        Crear Nueva Nota de Crédito (Devolución)
                    </Typography>
                    
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Las notas de crédito registran devoluciones de dinero al cliente y disminuyen los valores en caja.
                    </Alert>
                    
                    {/* Información del cliente */}
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="subtitle2">Cliente: {nombreCliente}</Typography>
                        <Typography variant="body2">Identidad: {identidad}</Typography>
                    </Box>
                    
                    {/* Motivo */}
                    <TextField
                        fullWidth
                        label="Motivo de la devolución"
                        value={nuevaNotaCreditoData.motivo || ''}
                        onChange={(e) => setNuevaNotaCreditoData(prev => ({
                            ...prev,
                            motivo: e.target.value
                        }))}
                        margin="normal"
                        multiline
                        rows={2}
                    />
                    
                    {/* Detalles de factura */}
                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                        Detalles de la Devolución
                    </Typography>
                    
                    {(nuevaNotaCreditoData.detalles || []).map((detalle, index) => (
                        <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Valor Original de Factura"
                                        value={detalle.valorOriginal || 0}
                                        InputProps={{ readOnly: true }}
                                        type="number"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Valor a Devolver"
                                        value={detalle.valorDevolucion || 0}
                                        onChange={(e) => handleUpdateDetalleNotaCredito(index, 'valorDevolucion', e.target.value)}
                                        type="number"
                                        inputProps={{ 
                                            step: "0.01", 
                                            min: "0", 
                                            max: nuevaNotaCreditoData.valorDisponible,
                                            sx: {
                                                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                                    display: 'none'
                                                },
                                                '& input[type=number]': {
                                                    MozAppearance: 'textfield'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            {(nuevaNotaCreditoData.detalles || []).length > 1 && (
                                <Button
                                    startIcon={<DeleteIcon />}
                                    color="error"
                                    onClick={() => handleEliminarDetalleNotaCredito(index)}
                                    sx={{ mt: 1 }}
                                >
                                    Eliminar
                                </Button>
                            )}
                        </Box>
                    ))}
                    
                    {/* Valor total de la nota */}
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                        <Typography variant="h6">
                            Valor Total a Devolver: -L. {parseFloat(nuevaNotaCreditoData.valorNotaCredito || 0).toFixed(2)}
                        </Typography>
                    </Box>
                    
                    {/* Documentos de devolución (métodos de pago inversos) */}
                    <Typography variant="subtitle1" gutterBottom>
                        Métodos de Devolución
                    </Typography>
                    
                    {(nuevaNotaCreditoData.documentosDevolucion || []).map((doc, index) => (
                        <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth>
                                        <InputLabel>Tipo de Documento</InputLabel>
                                        <Select
                                            value={doc.tipoDocumento || ''}
                                            onChange={(e) => handleUpdateDocumentoDevolucion(index, 'tipoDocumento', e.target.value)}
                                        >
                                            {transaccionesCajero.filter(t => t.descripcionTipoTransaccion !== 'PUNTOS LACM').map((tipo) => (
                                                <MenuItem key={tipo.id} value={tipo.id}>
                                                    {tipo.descripcionTipoTransaccion}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Descripción"
                                        value={doc.descripcion || ''}
                                        InputProps={{ readOnly: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Valor Devuelto"
                                        value={doc.valorDevolucion || 0}
                                        onChange={(e) => handleUpdateDocumentoDevolucion(index, 'valorDevolucion', e.target.value)}
                                        type="number"
                                        inputProps={{ 
                                            step: "0.01", 
                                            min: "0",
                                            sx: {
                                                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                                    display: 'none'
                                                },
                                                '& input[type=number]': {
                                                    MozAppearance: 'textfield'
                                                }
                                            }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <Button
                                startIcon={<DeleteIcon />}
                                color="error"
                                onClick={() => handleEliminarDocumentoDevolucion(index)}
                                sx={{ mt: 1 }}
                            >
                                Eliminar Método
                            </Button>
                        </Box>
                    ))}
                    
                    <Button
                        startIcon={<Add />}
                        variant="outlined"
                        onClick={handleAgregarDocumentoDevolucion}
                        sx={{ mb: 2 }}
                    >
                        Agregar Método de Devolución
                    </Button>
                    
                    {/* Resumen de documentos */}
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="body2">
                            <strong>Total en Métodos:</strong> -L. {
                                (nuevaNotaCreditoData.documentosDevolucion || [])
                                    .reduce((acc, doc) => acc + parseFloat(doc.valorDevolucion || 0), 0)
                                    .toFixed(2)
                            }
                        </Typography>
                        <Typography variant="body2">
                            <strong>Valor Nota:</strong> -L. {parseFloat(nuevaNotaCreditoData.valorNotaCredito || 0).toFixed(2)}
                        </Typography>
                        {Math.abs(
                            (nuevaNotaCreditoData.documentosDevolucion || [])
                                .reduce((acc, doc) => acc + parseFloat(doc.valorDevolucion || 0), 0) -
                            parseFloat(nuevaNotaCreditoData.valorNotaCredito || 0)
                        ) > 0.01 && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                Los métodos de devolución deben sumar exactamente el valor de la nota
                            </Alert>
                        )}
                    </Box>
                    
                    {/* Botones de acción */}
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button 
                            variant="contained" 
                            color="warning" 
                            onClick={handleCrearNotaCredito}
                            disabled={
                                (() => {
                                    const totalDocs = (nuevaNotaCreditoData.documentosDevolucion || []).reduce(
                                        (acc, doc) => acc + parseFloat(doc.valorDevolucion || 0), 
                                        0
                                    );
                                    const valorNota = parseFloat(nuevaNotaCreditoData.valorNotaCredito || 0);
                                    return Math.abs(totalDocs - valorNota) > 0.01 || valorNota <= 0;
                                })()
                            }
                        >
                            Crear Nota de Crédito
                        </Button>
                        <Button 
                            variant="outlined" 
                            color="secondary" 
                            onClick={() => {
                                setOpenNuevaNotaCredito(false);
                                setNuevaNotaCreditoData({
                                    idCliente: '',
                                    valorNotaCredito: 0,
                                    motivo: '',
                                    detalles: [],
                                    documentosDevolucion: []
                                });
                            }}
                        >
                            Cancelar
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Modal para mostrar detalles de tipos de transacciones/pagos */}
            <Modal 
                open={openDetallesPago} 
                onClose={() => {
                    if (editandoDetallesPago) {
                        Swal.fire({
                            icon: 'warning',
                            title: '¿Cancelar edición?',
                            text: 'Los cambios no guardados se perderán',
                            showCancelButton: true,
                            confirmButtonText: 'Sí, cancelar',
                            cancelButtonText: 'No'
                        }).then((result) => {
                            if (result.isConfirmed) {
                                handleCancelarEdicionDetalles();
                                setOpenDetallesPago(false);
                            }
                        });
                    } else {
                        setOpenDetallesPago(false);
                    }
                }}
                aria-labelledby="detalles-pago-modal"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '90%',
                    maxWidth: 1000,
                    bgcolor: 'background.paper',
                    border: '2px solid #000',
                    boxShadow: 24,
                    p: 4,
                    maxHeight: '90vh',
                    overflow: 'auto'
                }}>
                    <Typography variant="h6" component="h2" gutterBottom>
                        Detalles de Tipos de Transacciones - Factura {facturaDetallesPago?.numeroFactura}
                    </Typography>
                    
                    {!editandoDetallesPago && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Puede editar los métodos de pago y mover montos entre ellos. {!PERMITIR_MODIFICACION_LIBRE_MONTOS && 'El total debe mantenerse igual.'}
                        </Alert>
                    )}
                    
                    {editandoDetallesPago && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <strong>Modo de edición activo.</strong> Después de guardar, debe ejecutar la Partida Automática de Cierre Diario nuevamente.
                        </Alert>
                    )}
                    
                    {loadingDetallesPago ? (
                        <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress />
                            <Typography sx={{ ml: 2 }}>Cargando detalles de pago...</Typography>
                        </Box>
                    ) : (
                        <>
                            {(editandoDetallesPago ? detallesPagoEditados : detallesPago) && 
                             (editandoDetallesPago ? detallesPagoEditados : detallesPago).length > 0 ? (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Tipo de Transacción</strong></TableCell>
                                            <TableCell><strong>Número de Documento</strong></TableCell>
                                            <TableCell><strong>Valor</strong></TableCell>
                                            <TableCell><strong>Fecha Transacción</strong></TableCell>
                                            <TableCell><strong>Ingresado Por</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(editandoDetallesPago ? detallesPagoEditados : detallesPago).map((detalle, index) => (
                                            <TableRow key={detalle.Id || detalle.id || index}>
                                                <TableCell>
                                                    {editandoDetallesPago ? (
                                                        <FormControl fullWidth size="small">
                                                            <Select
                                                                value={detalle.IdTipoTransaccion || detalle.idTipoTransaccion}
                                                                onChange={(e) => handleActualizarDetallePago(index, 
                                                                    detalle.IdTipoTransaccion !== undefined ? 'IdTipoTransaccion' : 'idTipoTransaccion', 
                                                                    e.target.value)}
                                                            >
                                                                {transaccionesCajero && transaccionesCajero.length > 0 ? (
                                                                    transaccionesCajero.map((tipo) => (
                                                                        <MenuItem key={tipo.id} value={tipo.id}>
                                                                            {tipo.descripcionTipoTransaccion}
                                                                        </MenuItem>
                                                                    ))
                                                                ) : (
                                                                    <MenuItem value="" disabled>
                                                                        No hay tipos de documento disponibles
                                                                    </MenuItem>
                                                                )}
                                                            </Select>
                                                        </FormControl>
                                                    ) : (
                                                        detalle.DescripcionTipoTransaccion || 
                                                        detalle.descripcionTipoTransaccion || 
                                                        'N/A'
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {detalle.NumeroDocumento || 
                                                     detalle.numeroDocumento || 
                                                     'Sin documento'}
                                                </TableCell>
                                                <TableCell>
                                                    {editandoDetallesPago ? (
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            value={detalle.Valor || detalle.valor}
                                                            onChange={(e) => handleActualizarDetallePago(index, 
                                                                detalle.Valor !== undefined ? 'Valor' : 'valor', 
                                                                parseFloat(e.target.value))}
                                                            sx={{ width: 120 }}
                                                            InputProps={{
                                                                startAdornment: 'L. '
                                                            }}
                                                        />
                                                    ) : (
                                                        `L. ${parseFloat(detalle.Valor || detalle.valor || 0).toFixed(2)}`
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {detalle.FechaTransaccion || detalle.fechaTransaccion 
                                                        ? new Date(detalle.FechaTransaccion || detalle.fechaTransaccion).toLocaleString()
                                                        : (detalle.FechaIngreso || detalle.fechaIngreso 
                                                            ? new Date(detalle.FechaIngreso || detalle.fechaIngreso).toLocaleString()
                                                            : 'N/A')}
                                                </TableCell>
                                                <TableCell>
                                                    {(detalle.IngresadoPor || detalle.ingresadoPor || 'N/A').trim()}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                    <TableFooter>
                                        <TableRow>
                                            <TableCell colSpan={2}><strong>Total Pagado:</strong></TableCell>
                                            <TableCell>
                                                <strong>
                                                    L. {(editandoDetallesPago ? detallesPagoEditados : detallesPago).reduce((total, detalle) => 
                                                        total + parseFloat(detalle.Valor || detalle.valor || 0), 0
                                                    ).toFixed(2)}
                                                </strong>
                                                {!PERMITIR_MODIFICACION_LIBRE_MONTOS && editandoDetallesPago && !validarTotalDetallesPago() && (
                                                    <Typography color="error" variant="caption" display="block">
                                                        ⚠️ El total no coincide con el original
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell colSpan={2}></TableCell>
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            ) : (
                                <Typography variant="body1" align="center" sx={{ py: 4 }}>
                                    No se encontraron detalles de pago para esta factura.
                                </Typography>
                            )}
                        </>
                    )}
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                        <Box>
                            {!editandoDetallesPago && detallesPago.length > 0 && (
                                <Button 
                                    variant="outlined" 
                                    color="primary"
                                    onClick={handleEditarDetallesPago}
                                    disabled={loadingDetallesPago}
                                >
                                    ✏️ Editar Métodos de Pago
                                </Button>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {editandoDetallesPago ? (
                                <>
                                    <Button 
                                        variant="outlined" 
                                        onClick={handleCancelarEdicionDetalles}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button 
                                        variant="contained" 
                                        color="success"
                                        onClick={handleGuardarDetallesPago}
                                    >
                                        💾 Guardar Cambios
                                    </Button>
                                </>
                            ) : (
                                <Button 
                                    variant="contained" 
                                    onClick={() => setOpenDetallesPago(false)}
                                >
                                    Cerrar
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Modal>

        </div >
    )

}
export default GestionFactura;
